import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useVoiceCall } from './VoiceCallContext';
import ThemeToggle from './components/ThemeToggle';
import LanguageToggle from './components/LanguageToggle';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useOutsideClick, useLogout, getErrorMessage, clearProfileCache } from './common';
import { HiOutlineArrowRight, HiOutlinePhone, HiPhoneXMark } from 'react-icons/hi2';
import { HiOutlineChevronLeft, HiOutlineChevronRight } from 'react-icons/hi2';
import { GlobalStyle, DropdownItem, LogoutButton, DropdownMenu, MenuButton } from './styles/GlobalStyles';
import { useIsMobile } from './hooks/useIsMobile';
import { profileAPI, chatAPI, createWebSocket, wsEndpoints } from './api';
import { callAPI } from './utils/callApi';
import { SYSTEM_USER_ID } from './config';
import { refreshToken } from './utils/tokenUtils';
import {
  mergeIncomingMessage,
  mergeMessageListsPreferPlain,
  normalizeMessageId,
  nextClientSeq,
  shouldProcessMessageId,
  buildMessagePreview,
  buildSenderDisplayName,
} from './utils/incomingMessage';
import {
  decryptMessage,
  decryptMessages,
  resolveOwnPlaintext,
} from './utils/chatCrypto';
import {
  dropSession,
  encryptOutgoingText,
  encryptOutgoingVoice,
  bindOutboundVoiceMsgId,
  ensureE2E,
  replenishPrekeysIfNeeded,
} from './utils/e2e';
import { createChatBlinkController } from './utils/chatBlink';
import {
  clearChatBlink,
  configureChatBlinkContext,
  getPendingBlinkChatIds,
  markBlinkHandled,
  shouldBlinkIncomingMessage,
  subscribeChatBlink,
} from './utils/chatUnreadStore';
import { setActiveChatId, clearActiveChatId } from './utils/activeChat';
import {
  consumePendingOpenChatId,
  OPEN_CHAT_EVENT,
} from './utils/chatNavigation';
import {
  getSessionItem,
  getUiPref,
  purgeLegacySensitiveStorage,
  removeSessionItem,
  SESSION_KEYS,
  setUiPref,
  UI_PREFS_KEYS,
} from './utils/browserStorage';
import { useSystemWebSocket } from './SystemWebSocketContext';
import { useNotification } from './NotificationContext';
import { techColors } from './styles/TechRetroEffects';
import { useChatRequests } from './ChatRequestsContext';
import { useT } from './LocaleContext';
import ChatSidebar from './components/workspace/ChatSidebar';
import ChatHeader from './components/workspace/ChatHeader';
import ChatMessageInput from './components/workspace/ChatMessageInput';
import { extractPeaks } from './utils/voicePeaks';
import VoiceComposer from './components/workspace/VoiceComposer';
import MessageList from './components/workspace/MessageList';
import ChatRequestModal from './components/workspace/modals/ChatRequestModal';
import CreateGroupModal from './components/workspace/modals/CreateGroupModal';
import GroupInvitesModal from './components/workspace/modals/GroupInvitesModal';
import GroupInfoModal from './components/workspace/modals/GroupInfoModal';
import ChatRequestsListModal from './components/workspace/modals/ChatRequestsListModal';
import TrustPeerModal from './components/workspace/modals/TrustPeerModal';
import DeleteChatModal from './components/workspace/modals/DeleteChatModal';
import DeleteMessageModal from './components/workspace/modals/DeleteMessageModal';
import { 
  WorkspaceContainer,
  ChatContainer, 
  ChatTopBar,
  ChatTopBarPlaceholder,
  ChatPane,
  VoiceRoomBannerWrap,
  VoiceRoomBanner,
  VoiceRoomLiveDot,
  VoiceRoomBannerLabel,
  VoiceRoomJoinButton,
  ChatInputContainer, 
  InputAndButtonsContainer, 
  SendButton, 
  SidebarWrapper,
  SidebarToggleButton,
  CallHeaderButton,
  Resizer,
  EmptyChatState,
} from './styles/UserWorkspacePageStyles';



const MAX_MESSAGE_LENGTH = 5000;

const UserWorkspacePage = () => {
  const t = useT();
  const isMobile = useIsMobile();
  const { subscribe, send, isConnected } = useSystemWebSocket();
  const { showNotification: showToastNotification } = useNotification();
  const {
    incomingRequests,
    outgoingRequests,
    incomingCount,
    loadIncomingRequests,
    loadOutgoingRequests,
    removeIncomingRequest,
    setOutgoingRequests,
  } = useChatRequests();
  const {
    startCall,
    isInCall,
    endCall,
    updateCallProfiles,
    activeCall,
    startGroupRoom,
    joinGroupRoom,
    activeGroupRooms,
    setActiveGroupRooms,
  } = useVoiceCall();
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [favoritesChatId, setfavoritesChatId] = useState(null);
  const [chats, setChats] = useState([]);

  // Function to sort chats: favorites first, then by last_message_time (newest first)
  const sortChats = useCallback((chatsList, favoritesId) => {
    if (!chatsList || chatsList.length === 0) return chatsList;
    
    // Separate favorites and regular chats
    const favoritesChat = chatsList.find(chat =>
      chat.peer_name === 'favorites'
      || (favoritesId && String(chat.id) === String(favoritesId))
    );
    const regularChats = chatsList.filter(chat =>
      chat.peer_name !== 'favorites'
      && (!favoritesId || String(chat.id) !== String(favoritesId))
    );
    
    // Sort regular chats by last_message_time (newest first)
    regularChats.sort((a, b) => {
      const timeA = a.last_message_time ? new Date(a.last_message_time).getTime() : 0;
      const timeB = b.last_message_time ? new Date(b.last_message_time).getTime() : 0;
      return timeB - timeA; // Descending order (newest first)
    });
    
    // Combine: favorites first, then sorted regular chats
    const sortedChats = [];
    if (favoritesChat) {
      sortedChats.push(favoritesChat);
    }
    sortedChats.push(...regularChats);
    
    return sortedChats;
  }, []);

  const bumpChatToTop = useCallback((chatsList, chatId, favoritesId, lastMessageTime) => {
    if (!chatsList?.length || !chatId) {
      return chatsList;
    }

    const id = String(chatId);
    const favId = favoritesId ? String(favoritesId) : null;
    const now = lastMessageTime || new Date().toISOString();
    const chatIndex = chatsList.findIndex((chat) => String(chat.id) === id);

    if (chatIndex === -1) {
      return chatsList;
    }

    const updatedChat = { ...chatsList[chatIndex], last_message_time: now };
    const remaining = chatsList.filter((_, index) => index !== chatIndex);

    if (favId && id === favId) {
      return [updatedChat, ...remaining.filter((chat) => String(chat.id) !== favId)];
    }

    const favoritesChat = favId
      ? remaining.find((chat) => String(chat.id) === favId)
      : null;
    const others = favId
      ? remaining.filter((chat) => String(chat.id) !== favId)
      : remaining;

    if (favoritesChat) {
      return [favoritesChat, updatedChat, ...others];
    }

    return [updatedChat, ...others];
  }, []);


  const [selectedChatId, setSelectedChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [callHistory, setCallHistory] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [isLoadingChats, setIsLoadingChats] = useState(false);
  // Service connection status is now managed by global SystemWebSocket
  // We don't need local state for this anymore
  const [isLoading, setIsLoading] = useState(false);
  const [isChatContentReady, setIsChatContentReady] = useState(false);
  const [offset, setOffset] = useState(0);
  const [isChatRequestModalOpen, setIsChatRequestModalOpen] = useState(false);
  const [isChatMenuOpen, setIsChatMenuOpen] = useState(false);
  const [isChatRequestsModalOpen, setIsChatRequestsModalOpen] = useState(false);
  const [chatRequestsTab, setChatRequestsTab] = useState('incoming');
  const [chatRequestConnectId, setChatRequestConnectId] = useState('');
  const [chatRequestError, setChatRequestError] = useState('');
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [selectedCreateGroupContactIds, setSelectedCreateGroupContactIds] = useState([]);
  const [groupCreateError, setGroupCreateError] = useState('');
  const [isGroupInvitesModalOpen, setIsGroupInvitesModalOpen] = useState(false);
  const [groupInvites, setGroupInvites] = useState([]);
  const [groupInvitesCount, setGroupInvitesCount] = useState(0);
  const [isGroupInfoModalOpen, setIsGroupInfoModalOpen] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const [groupInfo, setGroupInfo] = useState(null);
  const [groupPendingInvites, setGroupPendingInvites] = useState([]);
  const [isGroupCreator, setIsGroupCreator] = useState(false);
  const chatMenuRef = useRef(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isChatsCollapsed, setIsChatsCollapsed] = useState(false);
  const [isDeleteChatModalOpen, setIsDeleteChatModalOpen] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [isDeleteMessageModalOpen, setIsDeleteMessageModalOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  const [isTrustPeerModalOpen, setIsTrustPeerModalOpen] = useState(false);
  const [blinkingChats, setBlinkingChats] = useState([]);
  const [unreadChatIds, setUnreadChatIds] = useState([]);
  const chatBlinkControllerRef = useRef(createChatBlinkController());
  const [chatToTrust, setChatToTrust] = useState(null);
  const [trustSettings, setTrustSettings] = useState({
    shareAvatar: false,
    shareEmail: false,
    shareStatus: false
  });
  // Store trust policies for all chats: { chatId: { shareAvatar, shareEmail, shareStatus } }
  const [chatTrustPolicies, setChatTrustPolicies] = useState({});
  const [openChatMenuId, setOpenChatMenuId] = useState(null);
  const chatItemMenuRefs = useRef({});
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = getUiPref(UI_PREFS_KEYS.sidebarWidth);
    return saved ? parseInt(saved, 10) : 350;
  });
  const [isResizing, setIsResizing] = useState(false);
  const [userAvatarUrl, setUserAvatarUrl] = useState(null);
  const [peerAvatars, setPeerAvatars] = useState({}); // { connect_id: avatarUrl }
  const userAvatarUrlRef = useRef(null);
  const peerAvatarsRef = useRef({});
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const chatLoadEpochRef = useRef(0);
  const pendingChatRevealRef = useRef(false);
  const loadingOlderRef = useRef(false);
  const wasMobileRef = useRef(isMobile);

  // Mobile list/detail: exclusive panes driven by isSidebarOpen.
  useEffect(() => {
    const wasMobile = wasMobileRef.current;
    wasMobileRef.current = isMobile;

    if (!isMobile) {
      if (wasMobile) {
        setIsSidebarOpen(true);
      }
      return;
    }

    if (!selectedChatId) {
      setIsSidebarOpen(true);
      return;
    }

    // Entering mobile with an open chat, or selecting a chat → show chat pane.
    if (!wasMobile || selectedChatId) {
      setIsSidebarOpen(false);
    }
  }, [isMobile, selectedChatId]);

  const handleMobileBackToChats = useCallback(() => {
    setIsSidebarOpen(true);
  }, []);

  const scrollMessagesToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }
    const overflow = container.scrollHeight - container.clientHeight;
    // Don't programmatically scroll until the list actually overflows —
    // iOS Safari bounces the pane when scrollTop is forced on a short chat.
    if (overflow <= 2) {
      if (container.scrollTop !== 0) {
        container.scrollTop = 0;
      }
      return;
    }
    const nextTop = overflow;
    if (Math.abs(container.scrollTop - nextTop) < 1) {
      return;
    }
    container.scrollTop = nextTop;
  }, []);

  // System WebSocket is now managed globally, no need for local ref
  const userSocketRef = useRef(null);
  const userSocketChatIdRef = useRef(null);
  const connectionTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const navigate = useNavigate();
  const handleLogout = useLogout();
  const dropdownRef = useRef(null);
  const chatsRef = useRef(chats);
  const processedMessageIdsRef = useRef(new Set());
  const loadedAvatarsRef = useRef(new Set());
  const noAvatarRef = useRef(new Set()); // Track users who don't have avatars
  const resizerRef = useRef(null);
  const isMountedRef = useRef(true);
  const hasRequestedInitialDataRef = useRef(false); // Track if initial data has been requested
  const loadingTrustPoliciesRef = useRef(new Set()); // Track which chat trust policies are currently loading
  const chatTrustPoliciesRef = useRef({}); // Keep ref of trust policies to check without causing re-renders

  const loadGroupInvites = useCallback(async () => {
    try {
      const response = await chatAPI.getGroupInvites();
      const invites = response.data?.invites || [];
      setGroupInvites(invites);
      setGroupInvitesCount(invites.length);
      return invites;
    } catch (error) {
      console.error('Failed to load group invites:', error);
      return [];
    }
  }, []);

  const loadGroupMembers = useCallback(async (chatId) => {
    const response = await chatAPI.getGroupMembers(chatId);
    const members = response.data?.members || [];
    const pendingInvites = response.data?.pending_invites || [];
    const info = response.data?.group || null;
    setGroupMembers(members);
    setGroupPendingInvites(pendingInvites);
    setGroupInfo(info);
    if (info?.title && chatId) {
      setChats((prev) => prev.map((chat) => (
        String(chat.id) === String(chatId) && chat.chat_type === 'group'
          ? { ...chat, title: info.title }
          : chat
      )));
    }
    setIsGroupCreator(
      members.some(
        (member) => member.is_creator && member.connect_id === user?.connect_id,
      ),
    );
    return { members, pendingInvites, group: info };
  }, [user?.connect_id]);

  const groupContacts = useMemo(() => {
    const seen = new Set();
    const contacts = [];
    for (const chat of chats) {
      const isUserChat = !chat.chat_type || chat.chat_type === 'user';
      if (!isUserChat || !chat.peer_connect_id) {
        continue;
      }
      if (chat.peer_name === 'favorites') {
        continue;
      }
      if (seen.has(chat.peer_connect_id)) {
        continue;
      }
      seen.add(chat.peer_connect_id);
      contacts.push({
        connect_id: chat.peer_connect_id,
        name: chat.peer_name || chat.peer_connect_id,
      });
    }
    return contacts.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [chats]);

  const creatableGroupContacts = useMemo(
    () => groupContacts.filter((contact) => contact.connect_id !== user?.connect_id),
    [groupContacts, user?.connect_id],
  );

  const inviteableGroupContacts = useMemo(() => {
    const memberIds = new Set(groupMembers.map((m) => m.connect_id).filter(Boolean));
    const pendingIds = new Set(groupPendingInvites.map((i) => i.connect_id).filter(Boolean));
    return groupContacts.filter(
      (contact) => contact.connect_id !== user?.connect_id
        && !memberIds.has(contact.connect_id)
        && !pendingIds.has(contact.connect_id),
    );
  }, [groupContacts, groupMembers, groupPendingInvites, user?.connect_id]);

  const groupSenderNames = useMemo(() => {
    const map = {};
    for (const member of groupMembers) {
      if (member.user_id) {
        map[String(member.user_id).toLowerCase()] = member.name;
      }
      if (member.connect_id) {
        map[member.connect_id] = member.name;
      }
    }
    for (const contact of groupContacts) {
      if (contact.connect_id && !map[contact.connect_id]) {
        map[contact.connect_id] = contact.name;
      }
    }
    return map;
  }, [groupMembers, groupContacts]);

  useEffect(() => {
    const selectedChat = chats.find((chat) => String(chat.id) === String(selectedChatId));
    if (!selectedChatId || selectedChat?.chat_type !== 'group') {
      return;
    }
    if (Object.keys(groupSenderNames).length === 0) {
      return;
    }
    setMessages((prev) => {
      let changed = false;
      const next = prev.map((msg) => {
        if (msg.sender_name) {
          return msg;
        }
        const senderIdKey = msg.sender_id ? String(msg.sender_id).toLowerCase() : '';
        const resolvedName = (senderIdKey && groupSenderNames[senderIdKey])
          || (msg.sender_connect_id && groupSenderNames[msg.sender_connect_id]);
        if (!resolvedName) {
          return msg;
        }
        changed = true;
        return { ...msg, sender_name: resolvedName };
      });
      return changed ? next : prev;
    });
  }, [groupSenderNames, selectedChatId, chats]);

  const startChatBlink = useCallback((chatId) => {
    const id = String(chatId);
    markBlinkHandled(id);
    setBlinkingChats((prev) => (
      prev.some((item) => String(item) === id) ? prev : [...prev, id]
    ));
    setUnreadChatIds((prev) => (
      prev.some((item) => String(item) === id) ? prev : [...prev, id]
    ));
    chatBlinkControllerRef.current.schedule(id, (expiredId) => {
      setBlinkingChats((prev) => prev.filter((item) => String(item) !== expiredId));
    });
  }, []);

  const stopChatBlink = useCallback((chatId) => {
    const id = chatBlinkControllerRef.current.clear(chatId);
    setBlinkingChats((prev) => prev.filter((item) => String(item) !== id));
    clearChatBlink(chatId);
  }, []);

  const markChatRead = useCallback((chatId) => {
    stopChatBlink(chatId);
    setUnreadChatIds((prev) => prev.filter((item) => String(item) !== String(chatId)));
  }, [stopChatBlink]);

  const applyIncomingPreview = useCallback((chatId, messageData) => {
    const id = String(chatId);
    const chat = chatsRef.current.find((item) => String(item.id) === id);
    const previewText = buildMessagePreview(messageData);
    const preview = chat?.chat_type === 'group'
      ? `${buildSenderDisplayName(messageData)}: ${previewText}`
      : previewText;
    setChats((prev) => prev.map((item) => (
      String(item.id) === id
        ? { ...item, last_message_preview: preview }
        : item
    )));
  }, []);

  const handleIncomingMessage = useCallback((messageData) => {
    const messageChatId = messageData?.chat_id;
    if (!messageChatId) {
      return false;
    }

    const messageId = messageData?.id;
    if (!shouldProcessMessageId(messageId, processedMessageIdsRef)) {
      return false;
    }

    const messageTime = messageData.created_at || new Date().toISOString();

    setChats((prevChats) => {
      const updatedChats = prevChats.map((chat) => {
        if (String(chat.id) === String(messageChatId)) {
          return {
            ...chat,
            last_message_time: messageTime,
          };
        }
        return chat;
      });
      return bumpChatToTop(updatedChats, messageChatId, favoritesChatId, messageTime);
    });

    const isActiveChat = String(messageChatId) === String(selectedChatIdRef.current);

    if (isActiveChat) {
      decryptMessage(messageData, { localUserId: userIdRef.current }).then((decrypted) => {
        if (String(messageChatId) !== String(selectedChatIdRef.current)) {
          return;
        }
        let finalMsg = decrypted;
        const ownId = userIdRef.current;
        const isOwn = ownId != null
          && String(messageData.sender_id).toLowerCase() === String(ownId).toLowerCase();
        if (
          isOwn
          && typeof finalMsg?.message === 'string'
          && (finalMsg.message.startsWith('🔒') || finalMsg.message.includes('cannot decrypt'))
        ) {
          const recovered = resolveOwnPlaintext(messageData);
          if (recovered) {
            finalMsg = {
              ...finalMsg,
              message: recovered,
              encrypted: true,
              e2e: true,
            };
          }
        }
        applyIncomingPreview(messageChatId, finalMsg);
        setMessages((prev) => {
          const result = mergeIncomingMessage(prev, finalMsg, messageId, processedMessageIdsRef);
          if (!result) {
            return prev;
          }
          if (result.shouldScroll) {
            shouldAutoScrollRef.current = true;
          }
          return result.messages;
        });
      });
    } else if (shouldBlinkIncomingMessage(messageData)) {
      startChatBlink(messageChatId);
      decryptMessage(messageData, { localUserId: userIdRef.current }).then((decrypted) => {
        applyIncomingPreview(messageChatId, decrypted);
      });
    }

    return true;
  }, [favoritesChatId, bumpChatToTop, startChatBlink, applyIncomingPreview]);

  const selectedChatIdRef = useRef(selectedChatId);
  const callHistoryAbortRef = useRef(null);
  const userIdRef = useRef(userId);
  const isGroupInfoModalOpenRef = useRef(isGroupInfoModalOpen);
  useEffect(() => {
    selectedChatIdRef.current = selectedChatId;
  }, [selectedChatId]);
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);
  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await ensureE2E();
        if (!cancelled) await replenishPrekeysIfNeeded();
      } catch (_) {
        // ignore bootstrap errors; send path will retry
      }
    })();
    return () => { cancelled = true; };
  }, [userId]);
  useEffect(() => {
    isGroupInfoModalOpenRef.current = isGroupInfoModalOpen;
  }, [isGroupInfoModalOpen]);

  const applyMessageDeleted = useCallback((deletedChatId, deletedMessageId) => {
    if (!deletedMessageId) {
      return;
    }

    const normalizedMessageId = normalizeMessageId(deletedMessageId);
    if (!normalizedMessageId) {
      return;
    }
    processedMessageIdsRef.current.delete(normalizedMessageId);

    // `messages` always belongs to the open chat — drop by id even if chat_id strings differ in format
    setMessages((prev) => {
      const next = prev.filter(
        (msg) => String(msg.id).toLowerCase() !== normalizedMessageId,
      );
      if (next.length === prev.length) {
        return prev;
      }
      return next;
    });
  }, []);

  // Обновляем ref при изменении chats
  useEffect(() => {
    chatsRef.current = chats;
  }, [chats]);

  useEffect(() => {
    configureChatBlinkContext({
      favoritesId: favoritesChatId,
      userId,
      connectId: user?.connect_id,
    });
  }, [favoritesChatId, userId, user?.connect_id]);

  useEffect(() => {
    if (!userId) {
      return;
    }
    getPendingBlinkChatIds().forEach((chatId) => {
      startChatBlink(chatId);
    });
  }, [userId, startChatBlink]);

  useEffect(() => {
    return subscribeChatBlink(() => {
      getPendingBlinkChatIds().forEach((chatId) => {
        startChatBlink(chatId);
      });
    });
  }, [startChatBlink]);

  useEffect(() => () => {
    chatBlinkControllerRef.current.dispose();
  }, []);

  useEffect(() => {
    purgeLegacySensitiveStorage();
  }, []);


  useEffect(() => {
    setActiveChatId(selectedChatId);
    return () => clearActiveChatId();
  }, [selectedChatId]);
  
  // Sort chats when favoritesChatId becomes available (after initial load)
  useEffect(() => {
    if (favoritesChatId && chats.length > 0) {
      setChats((prevChats) => sortChats(prevChats, favoritesChatId));
    }
  }, [favoritesChatId, sortChats, chats.length]);

  // Устанавливаем isMountedRef в true при монтировании и false при размонтировании
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Обработчики для изменения размера сайдбара
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing) return;
      
      const newWidth = e.clientX;
      const minWidth = 250;
      const maxWidth = window.innerWidth * 0.6; // Максимум 60% ширины экрана
      
      if (newWidth >= minWidth && newWidth <= maxWidth) {
        setSidebarWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Сохраняем ширину при изменении
  useEffect(() => {
    if (sidebarWidth) {
      setUiPref(UI_PREFS_KEYS.sidebarWidth, sidebarWidth.toString());
    }
  }, [sidebarWidth]);

  useEffect(() => {
    let isMounted = true;
    
    const fetchUserProfile = async () => {
      try {
        // Токен теперь в HttpOnly куках, не нужно проверять localStorage
        const response = await profileAPI.getProfile();
        
        if (isMounted) {
          // Reset initial data request flag when loading a new user
          // This ensures we request data even if the component was already mounted
          // Check if this is a new user session (chats are empty, which happens after logout)
          const currentChats = chatsRef.current || [];
          const isNewSession = currentChats.length === 0;
          
          if (isNewSession) {
            hasRequestedInitialDataRef.current = false;
          } else if (userId && userId !== response.data.user_id) {
            hasRequestedInitialDataRef.current = false;
          }
          
          setUser(response.data);
          setUserId(response.data.user_id);
          setfavoritesChatId(response.data.favorites_chat_id);

          // Загружаем аватар текущего пользователя, если он есть
          if (response.data.has_avatar) {
            try {
              // При первой загрузке используем кеш, но после обновления аватара обходим его
              const avatarResponse = await profileAPI.getAvatar();
              
              // Проверяем, что компонент еще смонтирован перед обработкой ответа
              if (!isMounted) return;
              
              // Check content-type to determine if response is JSON or blob
              const contentType = avatarResponse.headers['content-type'] || '';
              if (contentType.startsWith('image/')) {
                // Image blob response - convert arraybuffer to blob, then to object URL
                const avatarBlob = new Blob([avatarResponse.data], { type: contentType });
                const avatarObjectUrl = URL.createObjectURL(avatarBlob);
                if (isMounted) {
                  setUserAvatarUrl(avatarObjectUrl);
                }
              } else if (contentType.startsWith('application/json')) {
                // JSON response means "use default avatar" - parse from arraybuffer
                const decoder = new TextDecoder();
                const jsonText = decoder.decode(avatarResponse.data);
                const jsonData = JSON.parse(jsonText);
                if (jsonData.message === 'use default avatar' && isMounted) {
                  setUserAvatarUrl(null);
                }
              } else if (isMounted) {
                setUserAvatarUrl(null);
              }
            } catch (error) {
              if (isMounted) {
                setUserAvatarUrl(null);
              }
            }
          } else if (isMounted) {
            setUserAvatarUrl(null);
          }

          // Чаты загружаются через WebSocket подписку на 'chats_list' сообщения
        }
      } catch (error) {
        if (isMounted) {
          navigate('/login');
        }
      }
    };

    fetchUserProfile();
    
    // Слушаем события обновления аватара (когда аватар загружается в ProfilePage)
    const handleAvatarUpdate = async () => {
      if (!isMounted || !user) return;
      
      try {
        // Очищаем кеш и перезагружаем аватар
        profileAPI.clearAvatarCache();
        
        // Освобождаем старый URL
        if (userAvatarUrlRef.current) {
          URL.revokeObjectURL(userAvatarUrlRef.current);
        }
        
        // Загружаем новый аватар (обходим кеш)
        const avatarResponse = await profileAPI.getAvatar(null, true);
        if (!isMounted) return;
        
        const contentType = avatarResponse.headers['content-type'] || '';
        if (contentType.startsWith('image/')) {
          const avatarBlob = new Blob([avatarResponse.data], { type: contentType });
          const avatarObjectUrl = URL.createObjectURL(avatarBlob);
          if (isMounted) {
            setUserAvatarUrl(avatarObjectUrl);
          }
        } else if (contentType.startsWith('application/json')) {
          const decoder = new TextDecoder();
          const jsonText = decoder.decode(avatarResponse.data);
          const jsonData = JSON.parse(jsonText);
          if (jsonData.message === 'use default avatar' && isMounted) {
            setUserAvatarUrl(null);
          }
        } else if (isMounted) {
          setUserAvatarUrl(null);
        }
      } catch (error) {
        if (isMounted) {
          setUserAvatarUrl(null);
        }
      }
    };
    
    const handleProfileUpdate = async () => {
      if (!isMounted) return;

      try {
        clearProfileCache();
        const response = await profileAPI.getProfile();
        if (isMounted) {
          setUser(response.data);
          setUserId(response.data.user_id);
        }
      } catch (error) {
        console.error('Failed to refresh profile after name update:', error);
      }
    };

    window.addEventListener('avatarUpdated', handleAvatarUpdate);
    window.addEventListener('profileUpdated', handleProfileUpdate);
    
    // Cleanup: освобождаем память при размонтировании компонента
    return () => {
      isMounted = false;
      window.removeEventListener('avatarUpdated', handleAvatarUpdate);
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, sortChats]); // Removed 'user' dependency to avoid infinite loops
  
  // Обновляем refs при изменении значений
  useEffect(() => {
    userAvatarUrlRef.current = userAvatarUrl;
  }, [userAvatarUrl]);

  useEffect(() => {
    peerAvatarsRef.current = peerAvatars;
  }, [peerAvatars]);

  useEffect(() => {
    if (!isInCall) {
      return;
    }
    // Resolve peer from the call's chat, not the currently selected chat
    // (incoming calls often arrive while another chat is open).
    const callChatId = activeCall?.chat_id || selectedChatId;
    const chat = chats.find((item) => String(item.id) === String(callChatId));
    const peerConnectId = chat?.peer_connect_id;
    updateCallProfiles({
      selfName: user?.user_name || user?.name || user?.email?.split('@')[0] || 'you',
      selfId: userId,
      selfAvatarUrl: userAvatarUrl,
      peerAvatarUrl: peerConnectId ? (peerAvatars?.[peerConnectId] || null) : null,
      peerName: chat?.title || chat?.peer_name || activeCall?.peer_name || null,
    });
  }, [
    activeCall?.chat_id,
    activeCall?.peer_name,
    chats,
    isInCall,
    peerAvatars,
    selectedChatId,
    updateCallProfiles,
    user,
    userAvatarUrl,
    userId,
  ]);
  
  // Очистка URL при размонтировании компонента
  useEffect(() => {
    return () => {
      // Освобождаем URL аватара текущего пользователя
      if (userAvatarUrlRef.current) {
        try {
          URL.revokeObjectURL(userAvatarUrlRef.current);
        } catch (e) {
          // Ignore errors if URL was already revoked
        }
      }
      // Освобождаем URL всех аватаров собеседников
      Object.values(peerAvatarsRef.current).forEach(url => {
        if (url) {
          try {
          URL.revokeObjectURL(url);
          } catch (e) {
            // Ignore errors if URL was already revoked
          }
        }
      });
    };
  }, []); // Only clean up on unmount

  useEffect(() => {
    if (!user) {
      return;
    }

    if (getSessionItem(SESSION_KEYS.openIncomingRequests) === '1') {
      removeSessionItem(SESSION_KEYS.openIncomingRequests);
      setChatRequestsTab('incoming');
      setIsChatRequestsModalOpen(true);
      loadIncomingRequests();
      loadOutgoingRequests();
    }

    loadGroupInvites();

    const handleOpenIncomingRequests = () => {
      setChatRequestsTab('incoming');
      setIsChatRequestsModalOpen(true);
      loadIncomingRequests();
      loadOutgoingRequests();
    };

    window.addEventListener('openIncomingRequests', handleOpenIncomingRequests);

    return () => {
      window.removeEventListener('openIncomingRequests', handleOpenIncomingRequests);
    };
  }, [user, loadIncomingRequests, loadOutgoingRequests, loadGroupInvites]);

  // Update ref when trust policies change
  useEffect(() => {
    chatTrustPoliciesRef.current = chatTrustPolicies;
  }, [chatTrustPolicies]);

  // Load trust policies for all chats when chats are set
  // This loads peer's trust settings for current user (for displaying status)
  // IMPORTANT: Load trust policies FIRST before loading avatars to ensure privacy
  useEffect(() => {
    if (!chats || chats.length === 0) return;
    
    const loadTrustPolicies = async () => {
      const policies = {};
      const chatsToLoad = [];
      
      // First, identify which chats need trust policies loaded
      for (const chat of chats) {
        // Skip favorites and group chats (trust is 1:1 only)
        if (chat.peer_name === 'favorites' || chat.chat_type === 'group') continue;
        
        // Skip if already loading
        if (loadingTrustPoliciesRef.current.has(chat.id)) {
          continue; // Already loading, skip
        }
        
        // Check if we already have a policy for this chat (use ref to avoid dependency)
        if (chatTrustPoliciesRef.current[chat.id]) {
          continue; // Already loaded, skip
        }
        
        chatsToLoad.push(chat);
        loadingTrustPoliciesRef.current.add(chat.id);
      }
      
      // Load trust policies for chats that need them
      for (const chat of chatsToLoad) {
        try {
          const response = await chatAPI.getTrustSettings(chat.id, true);
          
          // Handle both response formats: {success: true, settings: {...}} or direct {share_avatar, share_email, share_status}
          let settings = null;
          if (response.data.success && response.data.settings) {
            // New format with wrapper
            settings = response.data.settings;
          } else if (response.data.share_avatar !== undefined || response.data.share_email !== undefined || response.data.share_status !== undefined) {
            // Direct format without wrapper
            settings = response.data;
          }
          
          if (settings) {
            policies[chat.id] = {
              shareAvatar: Boolean(settings.share_avatar),
              shareEmail: Boolean(settings.share_email),
              shareStatus: Boolean(settings.share_status)
            };
          } else {
            // Default: all false (peer doesn't share with me) - privacy first
            policies[chat.id] = {
              shareAvatar: false,
              shareEmail: false,
              shareStatus: false
            };
          }
        } catch (error) {
          console.error(`[Trust Policy] Error loading trust policy for chat ${chat.id}:`, error);
          // Default: all false (peer doesn't share with me) - privacy first
          policies[chat.id] = {
            shareAvatar: false,
            shareEmail: false,
            shareStatus: false
          };
        } finally {
          // Remove from loading set
          loadingTrustPoliciesRef.current.delete(chat.id);
        }
      }
      
      // Only update if we loaded any policies and component is still mounted
      if (Object.keys(policies).length > 0 && isMountedRef.current) {
        // Use functional update to merge with existing policies instead of overwriting
        // This prevents race conditions where multiple loads might overwrite correct values
        setChatTrustPolicies(prev => {
          const merged = { ...prev, ...policies };
          // Update ref as well
          chatTrustPoliciesRef.current = merged;
          return merged;
        });
      }
    };
    
    // Load trust policies immediately (no delay) to ensure privacy
    loadTrustPolicies();
  }, [chats]);

  // Load avatars for all chats when chats are set (e.g., after page refresh)
  // Also reload when trust policies change
  // IMPORTANT: Only load avatars AFTER trust policies are loaded to ensure privacy
  useEffect(() => {
    if (!chats || chats.length === 0 || !user) return;
    
    // Wait for trust policies to be loaded before loading avatars
    // If trust policies are not loaded yet, use default (all false) for privacy
    const loadAllPeerAvatars = async () => {
      // Clear avatars for chats where trust policy changed or is not loaded
      // This ensures we don't show avatars before trust policies are checked
      setPeerAvatars(prev => {
        const updated = { ...prev };
        let hasChanges = false;
        
        for (const chat of chats) {
          const peerConnectId = chat?.peer_connect_id;
          if (!peerConnectId || peerConnectId === user?.connect_id || chat.peer_name === 'favorites') {
            continue;
          }
          
          const trustPolicy = chatTrustPolicies[chat.id];
          // If trust policy is not loaded yet or avatar sharing is disabled, clear avatar
          if (!trustPolicy || !trustPolicy.shareAvatar) {
            if (updated[peerConnectId] !== null && updated[peerConnectId] !== undefined) {
              // Revoke blob URL if exists
              if (updated[peerConnectId] && updated[peerConnectId].startsWith('blob:')) {
                try {
                  URL.revokeObjectURL(updated[peerConnectId]);
                } catch (e) {
                  // Ignore errors if URL was already revoked
                }
              }
              updated[peerConnectId] = null;
              hasChanges = true;
            }
          }
        }
        
        return hasChanges ? updated : prev;
      });
      // Load avatars for all chats that don't have avatars loaded yet
      for (const chat of chats) {
        const peerConnectId = chat?.peer_connect_id;
        
        // Skip favorites chats and self
        if (!peerConnectId || 
            peerConnectId === user?.connect_id ||
            chat.peer_name === 'favorites') {
          continue;
        }
        
        // Check trust policy: does peer share avatar with me?
        // If trust policy is not loaded yet, assume false for privacy (don't show avatar)
        const trustPolicy = chatTrustPolicies[chat.id];
        
        if (!trustPolicy || !trustPolicy.shareAvatar) {
          // Trust policy not loaded yet OR peer doesn't share avatar - use default avatar
          // This ensures we never show avatar before trust policy is checked
          setPeerAvatars(prev => {
            // Only update if not already set to null
            if (prev[peerConnectId] !== null) {
              return { ...prev, [peerConnectId]: null };
            }
            return prev;
          });
          noAvatarRef.current.add(peerConnectId);
          // If trust policy is not loaded yet, don't load avatar - wait for trust policies
          if (!trustPolicy) {
            continue;
          }
          // If trust policy is loaded and shareAvatar is false, skip loading
          continue;
        }
        
        // Trust policy allows avatar sharing - check if we need to load it
        // If avatar was previously marked as "no avatar" but trust policy now allows it, clear the flag
        if (noAvatarRef.current.has(peerConnectId)) {
          noAvatarRef.current.delete(peerConnectId);
        }
        
        // Check if we already have the avatar loaded (including null for default avatar)
        // Use peerAvatarsRef.current to get the latest value
        const hasAvatarInfo = peerAvatarsRef.current.hasOwnProperty(peerConnectId);
        const currentAvatarUrl = peerAvatarsRef.current[peerConnectId];
        // Check if we're already loading this avatar
        const isLoading = loadedAvatarsRef.current.has(peerConnectId);
        
        // If avatar is null but trust policy allows it, we need to load it
        // Also clear loading flag if it's stuck (avatar is null but marked as loading)
        if (isLoading && currentAvatarUrl === null) {
          loadedAvatarsRef.current.delete(peerConnectId);
        }
        
        // Load avatar if:
        // 1. We don't have avatar info, OR
        // 2. We have avatar info but it's null and trust policy allows it (need to reload)
        const needsLoad = !hasAvatarInfo || (hasAvatarInfo && currentAvatarUrl === null);
        
        if (needsLoad && !loadedAvatarsRef.current.has(peerConnectId)) {
          // Mark as loading and make request
          loadedAvatarsRef.current.add(peerConnectId);
          
          try {
            const avatarResponse = await profileAPI.getAvatar(peerConnectId);
            // Check content-type to determine if response is JSON or blob
            const contentType = avatarResponse.headers['content-type'] || '';
            if (contentType.startsWith('application/json')) {
              // JSON response means "use default avatar" - parse from arraybuffer
              const decoder = new TextDecoder();
              const jsonText = decoder.decode(avatarResponse.data);
              const jsonData = JSON.parse(jsonText);
              if (jsonData.message === 'use default avatar') {
                noAvatarRef.current.add(peerConnectId);
                setPeerAvatars(prev => ({ ...prev, [peerConnectId]: null }));
              }
            } else if (contentType.startsWith('image/')) {
              // Image blob response - convert arraybuffer to blob, then to object URL
              // Проверяем, не создан ли уже blob URL для этого аватара
              const existingUrl = peerAvatarsRef.current[peerConnectId];
              if (existingUrl && existingUrl.startsWith('blob:')) {
                // Если blob URL уже существует, используем его (не создаем новый)
                loadedAvatarsRef.current.delete(peerConnectId);
                return;
              }
              const avatarBlob = new Blob([avatarResponse.data], { type: contentType });
              const avatarObjectUrl = URL.createObjectURL(avatarBlob);
              setPeerAvatars(prev => ({ ...prev, [peerConnectId]: avatarObjectUrl }));
              // Clear loading flag after successful load
              loadedAvatarsRef.current.delete(peerConnectId);
            } else {
              // Unknown content type for avatar
              loadedAvatarsRef.current.delete(peerConnectId);
            }
          } catch (error) {
            if (error?.response?.status === 404) {
              noAvatarRef.current.add(peerConnectId);
              setPeerAvatars(prev => ({ ...prev, [peerConnectId]: null }));
            } else {
              console.error(`[Avatar] Error loading avatar for chat ${chat.id} (${chat.peer_name}):`, error);
              loadedAvatarsRef.current.delete(peerConnectId);
            }
          }
        }
      }
    };
    
    // Load avatars immediately when trust policies are available
    // Trust policies are already loaded, so no delay needed
      loadAllPeerAvatars();
  }, [chats, user, chatTrustPolicies]); // Reload when trust policies change

  const applyChatsList = useCallback((chatsList) => {
    const incoming = Array.isArray(chatsList) ? chatsList : [];
    const hasFavorites = incoming.some((chat) => (
      chat.peer_name === 'favorites'
      || (favoritesChatId && String(chat.id) === String(favoritesChatId))
    ));
    const serviceChats = [];
    if (favoritesChatId && !hasFavorites) {
      serviceChats.push({ id: favoritesChatId, peer_name: 'favorites' });
    }
    const seenIds = new Set();
    const updatedChats = [...serviceChats, ...incoming].filter((chat) => {
      const id = String(chat.id);
      if (seenIds.has(id)) {
        return false;
      }
      seenIds.add(id);
      return true;
    });
    const sortedChats = sortChats(updatedChats, favoritesChatId);

    setChats((prevChats) => {
      const groupTitleById = {};
      const previewById = {};
      prevChats.forEach((chat) => {
        const id = String(chat.id);
        if (chat.chat_type === 'group' && chat.title) {
          groupTitleById[id] = chat.title;
        }
        if (chat.last_message_preview) {
          previewById[id] = chat.last_message_preview;
        }
      });

      const newChats = sortedChats.length > 0
        ? sortedChats
        : (prevChats.length > 0 ? prevChats : sortedChats);

      return newChats.map((chat) => {
        const id = String(chat.id);
        let merged = chat;
        if (chat.peer_name && chat.peer_name !== 'favorites' && chat.chat_type !== 'group') {
          merged = { ...merged, peer_name: chat.peer_name };
        }
        const prevGroupTitle = groupTitleById[id];
        if (
          merged.chat_type === 'group'
          && prevGroupTitle
          && prevGroupTitle !== merged.title
        ) {
          merged = { ...merged, title: prevGroupTitle };
        }
        if (previewById[id] && !merged.last_message_preview) {
          merged = { ...merged, last_message_preview: previewById[id] };
        }
        return merged;
      });
    });

    setIsLoadingChats(false);
  }, [favoritesChatId, sortChats]);

  const refreshChatsFromApi = useCallback(async () => {
    try {
      const response = await chatAPI.getChats();
      const chatsList = response.data?.chats;
      if (response.data?.success && Array.isArray(chatsList)) {
        applyChatsList(chatsList);
      }
    } catch (error) {
      console.error('Failed to refresh chats from API:', error);
    }
  }, [applyChatsList]);

  useEffect(() => {
    if (!userId) {
      return;
    }

    refreshChatsFromApi();
  }, [userId, refreshChatsFromApi]);
    

// Subscribe to global SystemWebSocket messages instead of creating local connection
  useEffect(() => {
    if (!user || !userId || !subscribe) {
      return;
    }

    // Subscribe to chats_list messages
    const unsubscribeChatsList = subscribe('chats_list', (data) => {
      const chatsList = data.data?.chats || [];
      applyChatsList(chatsList);
    });

    // Subscribe to new_chat_message messages
    const unsubscribeNewChatMessage = subscribe('new_chat_message', (data) => {
      // Always process via handleIncomingMessage — it dedupes by message id.
      // Do not skip based on "any" open chat socket: during chat switches the
      // socket ref can still belong to the previous chat and drop live messages.
      handleIncomingMessage(data.data);
    });

    // Subscribe to chat_created messages
    const unsubscribeChatCreated = subscribe('chat_created', (data) => {
      // Обработка создания нового чата - оптимистично добавляем чат сразу
      const chatData = data.data;
      if (chatData && chatData.chat_id) {
        setChats((prev) => {
          const existingChat = prev.find(c => String(c.id) === String(chatData.chat_id));
          if (existingChat) {
            return prev.map(chat =>
              String(chat.id) === String(chatData.chat_id)
                ? { ...chat, peer_name: chatData.peer_name || chat.peer_name, user_online: chatData.user_online || chat.user_online }
                : chat
            );
          }
          const newChat = {
            id: chatData.chat_id,
            peer_name: chatData.peer_name || 'Unknown',
            peer_connect_id: chatData.peer_connect_id,
            user_online: chatData.user_online || 'false',
            last_message_time: chatData.last_message_time || null
          };
          const updatedChats = prev.map(chat =>
            String(chat.id) === String(chatData.chat_id)
              ? { ...chat, ...newChat }
              : chat
          );
          const chatExists = updatedChats.some(chat => String(chat.id) === String(chatData.chat_id));
          if (!chatExists) {
            updatedChats.push(newChat);
          }
          return sortChats(updatedChats, favoritesChatId);
        });
      }
    });

    const unsubscribePeerDisplayNameUpdated = subscribe('peer_display_name_updated', (data) => {
      const updateData = data.data;
      const chatId = updateData?.chat_id;
      const peerName = updateData?.peer_name;
      if (!chatId || !peerName) {
        return;
      }
      setChats((prev) => prev.map((chat) => {
        if (String(chat.id) !== String(chatId)) {
          return chat;
        }
        return {
          ...chat,
          peer_name: peerName,
          ...(updateData.peer_connect_id ? { peer_connect_id: updateData.peer_connect_id } : {}),
        };
      }));
    });

    // Subscribe to user_online_status_changed messages
    const unsubscribeUserOnlineStatusChanged = subscribe('user_online_status_changed', (data) => {
      const statusData = data.data;
      if (!statusData) {
        return;
      }
      const chatId = statusData.chat_id;
      const userIdFromEvent = statusData.user_id;
      const connectIdFromEvent = statusData.connect_id;
      const isOnline = statusData.is_online === true || statusData.is_online === 'true';
      const onlineFlag = isOnline ? 'true' : 'false';

      if (typeof statusData.share_status === 'boolean' && chatId) {
        setChatTrustPolicies((prev) => {
          const key = Object.keys(prev).find((item) => String(item) === String(chatId));
          if (!key) {
            return prev;
          }
          const current = prev[key];
          if (current.shareStatus === statusData.share_status) {
            return prev;
          }
          const next = {
            ...prev,
            [key]: { ...current, shareStatus: statusData.share_status },
          };
          chatTrustPoliciesRef.current = next;
          return next;
        });
      }

      if (chatId) {
        setChats((prev) => {
          const chat = prev.find((item) => String(item.id) === String(chatId));
          if (!chat || chat.chat_type === 'group') {
            return prev;
          }
          if (chat.user_online === onlineFlag) {
            return prev;
          }
          return prev.map((item) => (
            String(item.id) === String(chatId)
              ? { ...item, user_online: onlineFlag }
              : item
          ));
        });
      }

      if (userIdFromEvent || connectIdFromEvent) {
        setGroupMembers((prev) => {
          if (!prev.length) {
            return prev;
          }
          let changed = false;
          const next = prev.map((member) => {
            const sameUser = userIdFromEvent && String(member.user_id) === String(userIdFromEvent);
            const sameConnect = connectIdFromEvent && String(member.connect_id) === String(connectIdFromEvent);
            if (!sameUser && !sameConnect) {
              return member;
            }
            if (member.user_online === onlineFlag) {
              return member;
            }
            changed = true;
            return { ...member, user_online: onlineFlag };
          });
          return changed ? next : prev;
        });
      }
    });

    // Subscribe to chat_deleted messages
    // Subscribe to chat_deleted messages
    const unsubscribeChatDeleted = subscribe('chat_deleted', (data) => {
                const deleteData = data.data;
                const deletedChatId = deleteData.chat_id;
                stopChatBlink(deletedChatId);
                setUnreadChatIds((prev) => prev.filter((item) => String(item) !== String(deletedChatId)));
                clearChatBlink(deletedChatId);
                setChats((prev) => prev.filter(chat => chat.id !== deletedChatId));
                      // Если удаленный чат был выбран, переключаемся на favorites чат
                if (selectedChatId === deletedChatId) {
        if (favoritesChatId) {
                    setSelectedChatId(favoritesChatId);
                  } else {
                    setSelectedChatId(null);
                  }
                  setMessages([]);
                  setCallHistory([]);
                }
    });

    const unsubscribeMessageDeleted = subscribe('message_deleted', (data) => {
      const deleteData = data.data;
      applyMessageDeleted(deleteData?.chat_id, deleteData?.message_id);
    });

    // Subscribe to chat_restored messages
    const unsubscribeChatRestored = subscribe('chat_restored', (data) => {
                const restoreData = data.data;
                // Добавляем чат обратно в список, если его там нет
                if (restoreData && restoreData.chat_id) {
                  setChats((prev) => {
                    const existingChat = prev.find(c => String(c.id) === String(restoreData.chat_id));
                    if (existingChat) {
                      return prev; // Чат уже есть
                    }
                    // Добавляем восстановленный чат
                    return [...prev, {
                      id: restoreData.chat_id,
                      peer_connect_id: restoreData.peer_connect_id,
                      peer_name: restoreData.peer_name,
                      user_online: "false"
                    }];
                  });
                }
    });

    const unsubscribeChatStatusChanged = subscribe('chat_status_changed', (data) => {
      const statusData = data.data;
      const chatId = statusData?.chat_id;
      const chatStatus = statusData?.status;
      if (!chatId || !chatStatus) {
        return;
      }

      setChats((prev) => prev.map((chat) => (
        String(chat.id) === String(chatId) ? { ...chat, status: chatStatus } : chat
      )));
    });

    const unsubscribeGroupCreated = subscribe('group_created', (data) => {
      const groupData = data.data;
      if (!groupData?.chat_id) {
        return;
      }
      setChats((prev) => {
        if (prev.find((chat) => String(chat.id) === String(groupData.chat_id))) {
          return prev;
        }
        return sortChats([
          ...prev,
          {
            id: groupData.chat_id,
            chat_type: 'group',
            title: groupData.title || 'group',
            member_count: groupData.member_count || 1,
            last_message_time: null,
          },
        ], favoritesChatId);
      });
    });

    const unsubscribeGroupInvite = subscribe('group_invite', async (data) => {
      await loadGroupInvites();
      const payload = data?.data;
      if (payload?.title) {
        showToastNotification(t('system.newGroupInvite'), 'info');

      }
    });

    const unsubscribeGroupMemberJoined = subscribe('group_member_joined', (data) => {
      const payload = data.data;
      if (!payload?.chat_id) {
        return;
      }
      setChats((prev) => prev.map((chat) => {
        if (String(chat.id) !== String(payload.chat_id)) {
          return chat;
        }
        const nextCount = (chat.member_count || 0) + 1;
        return { ...chat, member_count: nextCount, title: payload.title || chat.title };
      }));
      if (
        isGroupInfoModalOpenRef.current &&
        String(selectedChatIdRef.current) === String(payload.chat_id)
      ) {
        loadGroupMembers(payload.chat_id);
      }
    });

    const unsubscribeGroupMemberLeft = subscribe('group_member_left', (data) => {
      const payload = data.data;
      if (!payload?.chat_id) {
        return;
      }
      if (payload.connect_id && user?.connect_id && payload.connect_id === user.connect_id) {
        setChats((prev) => prev.filter((chat) => String(chat.id) !== String(payload.chat_id)));
        if (String(selectedChatIdRef.current) === String(payload.chat_id)) {
          setSelectedChatId(null);
          setMessages([]);
          setCallHistory([]);
        }
        setIsGroupInfoModalOpen(false);
        return;
      }
      setChats((prev) => prev.map((chat) => {
        if (String(chat.id) !== String(payload.chat_id)) {
          return chat;
        }
        return {
          ...chat,
          member_count: Math.max((chat.member_count || 1) - 1, 0),
        };
      }));
      if (
        isGroupInfoModalOpenRef.current &&
        String(selectedChatIdRef.current) === String(payload.chat_id)
      ) {
        loadGroupMembers(payload.chat_id);
      }
    });

    const unsubscribeGroupRenamed = subscribe('group_renamed', (data) => {
      const payload = data.data;
      if (!payload?.chat_id || !payload?.title) {
        return;
      }
      setChats((prev) => prev.map((chat) => (
        String(chat.id) === String(payload.chat_id)
          ? { ...chat, title: payload.title }
          : chat
      )));
      setGroupInfo((prev) => (
        prev && String(selectedChatIdRef.current) === String(payload.chat_id)
          ? { ...prev, title: payload.title }
          : prev
      ));
    });

    const unsubscribeGroupInviteCancelled = subscribe('group_invite_cancelled', async (data) => {
      const payload = data.data;
      await loadGroupInvites();
      if (payload?.title) {
        showToastNotification('a group invite was cancelled', 'info');
      }
      if (
        payload?.chat_id &&
        isGroupInfoModalOpenRef.current &&
        String(selectedChatIdRef.current) === String(payload.chat_id)
      ) {
        loadGroupMembers(payload.chat_id);
      }
    });

    // Request initial data through WebSocket after subscriptions are set up
    // This will trigger send_initial_data on the backend
    const requestInitialData = () => {
      if (!send || !isConnected) {
        return false;
      }
      
      // Check if WebSocket is connected
      if (!isConnected()) {
        return false;
      }
      
      // Don't request if already requested in this session
      if (hasRequestedInitialDataRef.current) {
        return true;
      }
      
      const sent = send({ type: 'request_initial_data' });
      if (sent) {
        hasRequestedInitialDataRef.current = true;
        // Показываем спиннер загрузки, если чаты еще пусты
        if (chats.length === 0) {
          setIsLoadingChats(true);
        }
      }
      return sent;
    };

    // Subscribe to WebSocket connection event to request data immediately when connected
    const unsubscribeWsConnected = subscribe('ws_connected', () => {
      // When WebSocket connects, immediately try to request initial data
      // Only if user and userId are loaded (they should be at this point since useEffect depends on them)
      if (!hasRequestedInitialDataRef.current && isConnected && isConnected() && user && userId) {
        requestInitialData();
      }
    });

    // Try to request initial data immediately if WebSocket is already connected
    // This handles the case when WebSocket was already connected before user loaded or subscriptions were set up
    // Use queueMicrotask to ensure subscriptions are fully set up, but don't delay unnecessarily
    queueMicrotask(() => {
      if (isConnected && isConnected() && !hasRequestedInitialDataRef.current && user && userId) {
        requestInitialData();
      }
    });

    // Try to request initial data with retries
    // This ensures we request data even if WebSocket connection timing is off
    let retryCount = 0;
    const maxRetries = 50; // Increased retries to ensure we get the data
    const retryInterval = 100; // Reduced interval for faster retries
    
    const tryRequestInitialData = () => {
      if (hasRequestedInitialDataRef.current) {
        return; // Already requested, stop retrying
      }
      
      if (retryCount >= maxRetries) {
        // If we've exhausted retries, try one more time after a longer delay
        // This handles edge cases where WebSocket connects very late
        setTimeout(() => {
          if (!hasRequestedInitialDataRef.current && isConnected && isConnected() && user && userId) {
            requestInitialData();
          }
        }, 1000);
        return;
      }
      
      retryCount++;
      const success = requestInitialData();
      
      if (!success && user && userId) {
        // Retry after a delay only if user is loaded
        setTimeout(tryRequestInitialData, retryInterval);
      }
    };

    // Start requesting immediately (no delay) to ensure fast response
    // Only try if user and userId are loaded
    if (user && userId) {
      tryRequestInitialData();
    }
    
    // Also set up an interval to check WebSocket connection and request data when connected
    // This handles the case when WebSocket connects after all retries are exhausted
    const connectionCheckInterval = setInterval(() => {
      if (!hasRequestedInitialDataRef.current && isConnected && isConnected() && user && userId) {
        const success = requestInitialData();
        if (success) {
          clearInterval(connectionCheckInterval);
        }
      }
    }, 200); // Check more frequently (every 200ms instead of 500ms)
    
    // Cleanup interval after 15 seconds to avoid infinite checking (increased from 10s)
    const maxCheckTimeout = setTimeout(() => {
      clearInterval(connectionCheckInterval);
    }, 15000);

    // Cleanup: unsubscribe from all messages
    return () => {
      clearTimeout(maxCheckTimeout);
      clearInterval(connectionCheckInterval);
      unsubscribeChatsList();
      unsubscribeNewChatMessage();
      unsubscribeChatCreated();
      unsubscribePeerDisplayNameUpdated();
      unsubscribeUserOnlineStatusChanged();
      unsubscribeChatDeleted();
      unsubscribeMessageDeleted();
      unsubscribeChatRestored();
      unsubscribeChatStatusChanged();
      unsubscribeGroupCreated();
      unsubscribeGroupInvite();
      unsubscribeGroupMemberJoined();
      unsubscribeGroupMemberLeft();
      unsubscribeGroupRenamed();
      unsubscribeGroupInviteCancelled();
      unsubscribeWsConnected();
    };
  }, [user, userId, favoritesChatId, selectedChatId, subscribe, send, isConnected, sortChats, chats.length, handleIncomingMessage, applyMessageDeleted, loadGroupInvites, loadGroupMembers, user?.connect_id, showToastNotification, applyChatsList, stopChatBlink]);

  // Additional safety check: if chats are still empty after user loads, request initial data again
  useEffect(() => {
    if (!user || !userId) {
      return;
    }

    const chatsLength = chats.length;

    // Если чаты загружены, скрываем спиннер
    if (chatsLength > 0) {
      setIsLoadingChats(false);
      return;
    }

    // Показываем спиннер, если user загружен, но чаты пусты
    setIsLoadingChats(true);

    // Timeout to hide spinner if no response received (10 seconds)
    const spinnerTimeout = setTimeout(() => {
      setIsLoadingChats(false);
    }, 10000);

    // If chats are still empty 2 seconds after user loads, try requesting initial data again
    // This handles edge cases where the initial request might have failed
    const fallbackTimeout = setTimeout(() => {
      if (chatsLength === 0 && isConnected && isConnected() && !hasRequestedInitialDataRef.current) {
        const sent = send && send({ type: 'request_initial_data' });
        if (sent) {
          hasRequestedInitialDataRef.current = true;
        }
      } else if (chatsLength === 0 && isConnected && isConnected()) {
        // Even if flag is set, try one more time if chats are still empty
        send && send({ type: 'request_initial_data' });
      }
    }, 2000);

    return () => {
      clearTimeout(fallbackTimeout);
      clearTimeout(spinnerTimeout);
    };
  }, [user, userId, chats.length, isConnected, send]);


// USER WEB SOCKET — only the currently selected chat keeps an open connection
  useEffect(() => {
    // Не подключаемся к WebSocket, если пользователь не загружен или нет selectedChatId
    // Исключаем favorites чат (работает через обычный WebSocket)
    if (!user || !userId || !selectedChatId) return;

    let isMounted = true;

    const closeChatSocket = (socket) => {
      if (!socket) {
        return;
      }
      socket.onerror = null;
      socket.onopen = null;
      socket.onclose = null;
      socket.onmessage = null;
      if (
        socket.readyState === WebSocket.OPEN
        || socket.readyState === WebSocket.CONNECTING
      ) {
        try {
          socket.close();
        } catch (error) {
          // ignore close races
        }
      }
    };

    // Закрываем предыдущее соединение, если оно существует
    closeChatSocket(userSocketRef.current);
    userSocketRef.current = null;
    userSocketChatIdRef.current = null;
    
    const connectUserWebSocket = async () => {
      // Проверяем количество попыток переподключения
      if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        // Максимальное количество попыток достигнуто - прекращаем переподключение
        reconnectAttemptsRef.current = 0; // Сбрасываем счетчик после достижения максимума
        return;
      }

      const chatIdForSocket = selectedChatId;
      
      // Получаем токен для WebSocket подключения
      let token;
      try {
        token = await refreshToken();
        if (!token) {
          navigate('/login');
          return;
        }
      } catch (error) {
        navigate('/login');
        return;
      }

      // Chat may have changed while awaiting token
      if (!isMounted || String(selectedChatIdRef.current) !== String(chatIdForSocket)) {
        return;
      }
      
      const socket = createWebSocket(wsEndpoints.chat(chatIdForSocket), token);
      closeChatSocket(userSocketRef.current);
      userSocketRef.current = socket;
      userSocketChatIdRef.current = chatIdForSocket;

      // Таймаут для подключения (5 секунд)
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
      connectionTimeoutRef.current = setTimeout(() => {
        if (socket.readyState === WebSocket.CONNECTING || socket.readyState === WebSocket.CLOSED) {
          socket.close();
          // Попытка переподключения через 1 секунду
          reconnectAttemptsRef.current += 1;
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            setTimeout(() => {
              if (isMounted && selectedChatId) {
                connectUserWebSocket();
              }
            }, 1000);
          } else {
            reconnectAttemptsRef.current = 0; // Сбрасываем после достижения максимума
          }
        }
      }, 5000);

      socket.onopen = () => {
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        // WebSocket подключен - сбрасываем счетчик попыток
        reconnectAttemptsRef.current = 0;
      };

      socket.onmessage = (event) => {
        if (!isMounted) return;
        
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'error') {
            showToastNotification(data.message || 'chat connection error', 'error', 10000);
            return;
          }

          if (data.type === 'message_deleted') {
            applyMessageDeleted(data.data?.chat_id, data.data?.message_id);
            return;
          }

          if (data.type === 'message') {
            // Drop events from a stale socket after chat switch
            if (String(userSocketChatIdRef.current) !== String(selectedChatIdRef.current)) {
              return;
            }
            // USER WebSocket используется для отправки и получения сообщений конкретного чата
            const messageChatId = data.messages?.chat_id;
            const isActiveChat = messageChatId
              && String(messageChatId) === String(selectedChatIdRef.current);
            
            // File message received via WebSocket
            
            // Если чат активен, обрабатываем сообщение
            // Для favorites чата также обрабатываем, если он выбран
            if (isActiveChat || (messageChatId && String(messageChatId) === String(favoritesChatId) && String(selectedChatIdRef.current) === String(favoritesChatId))) {
              const senderConnectId = data.messages?.sender_connect_id;
              const isSystemMessage = data.messages?.sender === 'system' || 
                                     data.messages?.sender_id === SYSTEM_USER_ID ||
                                     !data.messages?.sender_connect_id;
              
              if (senderConnectId && 
                  !isSystemMessage &&
                  senderConnectId !== user?.connect_id &&
                  !peerAvatarsRef.current.hasOwnProperty(senderConnectId) &&
                  !loadedAvatarsRef.current.has(senderConnectId) && 
                  !noAvatarRef.current.has(senderConnectId)) {
                loadedAvatarsRef.current.add(senderConnectId);
                profileAPI.getAvatar(senderConnectId)
                  .then(avatarResponse => {
                    const contentType = avatarResponse.headers['content-type'] || '';
                    if (contentType.startsWith('application/json')) {
                      const decoder = new TextDecoder();
                      const jsonText = decoder.decode(avatarResponse.data);
                      const jsonData = JSON.parse(jsonText);
                      if (jsonData.message === 'use default avatar') {
                        noAvatarRef.current.add(senderConnectId);
                        setPeerAvatars(prev => ({ ...prev, [senderConnectId]: null }));
                      }
                    } else if (contentType.startsWith('image/')) {
                      const existingUrl = peerAvatarsRef.current[senderConnectId];
                      if (existingUrl && existingUrl.startsWith('blob:')) {
                        return;
                      }
                      const avatarBlob = new Blob([avatarResponse.data], { type: contentType });
                      const avatarObjectUrl = URL.createObjectURL(avatarBlob);
                      setPeerAvatars(prev => ({ ...prev, [senderConnectId]: avatarObjectUrl }));
                    }
                  })
                  .catch(error => {
                    if (error?.response?.status === 404) {
                      noAvatarRef.current.add(senderConnectId);
                      setPeerAvatars(prev => ({ ...prev, [senderConnectId]: null }));
                    } else {
                      console.error('Error loading avatar:', error);
                      loadedAvatarsRef.current.delete(senderConnectId);
                    }
                  });
              }

              handleIncomingMessage(data.messages);
            }
          }
        } catch (error) {
          // Тихая обработка ошибок парсинга сообщений
        }
      };
      
      socket.onclose = (event) => {
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        // WebSocket отключен
        // Переподключаемся только если это не было нормальное закрытие
        if (event.code !== 1000 && isMounted && selectedChatId) {
          reconnectAttemptsRef.current += 1;
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            setTimeout(() => {
              if (isMounted && selectedChatId) {
                connectUserWebSocket();
              }
            }, 2000);
          } else {
            reconnectAttemptsRef.current = 0; // Сбрасываем после достижения максимума
          }
        } else {
          // Нормальное закрытие - сбрасываем счетчик
          reconnectAttemptsRef.current = 0;
        }
      };
      
      socket.onerror = (error) => {
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        // Ошибка подключения - переподключаемся через 2 секунды
        reconnectAttemptsRef.current += 1;
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          setTimeout(() => {
            if (isMounted && selectedChatId) {
              connectUserWebSocket();
            }
          }, 2000);
        } else {
          reconnectAttemptsRef.current = 0; // Сбрасываем после достижения максимума
        }
      };
    };
    
    connectUserWebSocket();
    
    return () => {
      isMounted = false;
      reconnectAttemptsRef.current = 0; // Сбрасываем счетчик при размонтировании
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }
      closeChatSocket(userSocketRef.current);
      userSocketRef.current = null;
      userSocketChatIdRef.current = null;
    };
  }, [user, userId, selectedChatId, favoritesChatId, navigate, sortChats, handleIncomingMessage, applyMessageDeleted, showToastNotification]);


  const fetchMessages = useCallback(async (chatId, offset = 0) => {
    try {
      const response = await chatAPI.getChatMessages(chatId, 20, offset);
      const messages = (response.data.messages || []).map((msg) => (
        msg?.chat_id ? msg : { ...msg, chat_id: chatId }
      ));
      return decryptMessages(messages, chatId, { localUserId: userIdRef.current });
    } catch (error) {

      return [];
    }
  }, []);

  const fetchCallHistory = useCallback(async (chatId, signal) => {
    const chat = chatsRef.current.find((item) => String(item.id) === String(chatId));
    if (!chat || chat.peer_name === 'favorites') {
      return [];
    }
    try {
      const response = await callAPI.getHistory({ chatId, limit: 100, signal });
      return response.data?.items || [];
    } catch (error) {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError') {
        return null;
      }
      console.warn('[call] failed to load call history:', error?.message || error);
      return [];
    }
  }, []);

  const refreshCallHistory = useCallback(async (chatId = selectedChatIdRef.current) => {
    if (!chatId) {
      setCallHistory([]);
      return;
    }

    if (callHistoryAbortRef.current) {
      callHistoryAbortRef.current.abort();
    }
    const controller = new AbortController();
    callHistoryAbortRef.current = controller;

    const items = await fetchCallHistory(chatId, controller.signal);
    if (items === null) {
      return;
    }
    if (callHistoryAbortRef.current === controller
      && String(chatId) === String(selectedChatIdRef.current)) {
      setCallHistory(items);
      callHistoryAbortRef.current = null;
    }
  }, [fetchCallHistory]);

  useEffect(() => {
    if (!subscribe) {
      return undefined;
    }

    const refreshCallsForActiveChat = (event) => {
      const chatId = event?.data?.chat_id;
      if (chatId && String(chatId) === String(selectedChatIdRef.current)) {
        refreshCallHistory(chatId);
      }
    };

    const unsubscribeCallEnded = subscribe('call_ended', refreshCallsForActiveChat);
    const unsubscribeCallMissed = subscribe('call_missed', refreshCallsForActiveChat);
    const unsubscribeCallRejected = subscribe('call_rejected', refreshCallsForActiveChat);
    const unsubscribeGroupCallEnded = subscribe('group_call_ended', refreshCallsForActiveChat);

    return () => {
      unsubscribeCallEnded();
      unsubscribeCallMissed();
      unsubscribeCallRejected();
      unsubscribeGroupCallEnded();
    };
  }, [subscribe, refreshCallHistory]);



  const handleScroll = async (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    
    // Обновляем флаг автоматической прокрутки
    const atBottom = scrollHeight - scrollTop - clientHeight < 100;
    if (!loadingOlderRef.current) {
      shouldAutoScrollRef.current = atBottom;
    }
    
    if (scrollTop <= 1 && !isLoading && !loadingOlderRef.current) {
      setIsLoading(true);
      loadingOlderRef.current = true;
      const chatIdForPage = selectedChatIdRef.current;
      const epochForPage = chatLoadEpochRef.current;
      const newOffset = offset + 20;
      try {
        const response = await chatAPI.getChatMessages(chatIdForPage, 20, newOffset);
        if (
          epochForPage !== chatLoadEpochRef.current
          || String(selectedChatIdRef.current) !== String(chatIdForPage)
        ) {
          return;
        }
        const raw = (response.data.messages || []).map((msg) => (
          msg?.chat_id ? msg : { ...msg, chat_id: chatIdForPage }
        ));
        const newMessages = await decryptMessages(raw, chatIdForPage, {
          localUserId: userIdRef.current,
        });
        if (!newMessages.length) {
          return;
        }
        const previousScrollHeight = scrollHeight;
        const scrollContainer = e.target;

        queueMicrotask(() => {
          if (
            epochForPage !== chatLoadEpochRef.current
            || String(selectedChatIdRef.current) !== String(chatIdForPage)
          ) {
            return;
          }
          flushSync(() => {
            setMessages((prevMessages) => {
              const existingIds = new Set(
                prevMessages.map((msg) => normalizeMessageId(msg.id)).filter(Boolean),
              );
              const uniqueNewMessages = newMessages.filter((msg) => {
                const normalizedId = normalizeMessageId(msg.id);
                return normalizedId && !existingIds.has(normalizedId);
              });
              return [...uniqueNewMessages, ...prevMessages];
            });
            setOffset(newOffset);
          });
        });

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (scrollContainer) {
              scrollContainer.scrollTop = scrollContainer.scrollHeight - previousScrollHeight;
            }
          });
        });
      } catch (error) {
        // Тихая обработка ошибок загрузки старых сообщений
      } finally {
        loadingOlderRef.current = false;
        setIsLoading(false);
      }
    }
  };


  useOutsideClick(dropdownRef, () => setMenuOpen(false));
  const toggleMenu = () => setMenuOpen((prev) => !prev);

  const handleSendMessage = async () => {
    if (!currentMessage.trim()) return;

    if (currentMessage.trim() && currentMessage.length > MAX_MESSAGE_LENGTH) {
      showToastNotification(`message is too long. maximum length is ${MAX_MESSAGE_LENGTH} characters.`, 'warning', 10000);
      return;
    }

    const socket = userSocketRef.current;

    if (socket && socket.readyState === WebSocket.OPEN) {
      const messageText = currentMessage.trim();
      const tempId = `temp-${Date.now()}-${Math.random()}`;
      let wireText = messageText;
      let e2eVersion = null;
      let e2eHeader = null;
      const activeChat = chats.find((c) => String(c.id) === String(selectedChatId));
      const isGroup = activeChat?.chat_type === 'group';
      const isFavorites = activeChat?.peer_name === 'favorites'
        || (favoritesChatId && String(selectedChatId) === String(favoritesChatId));

      try {
        const sealed = await encryptOutgoingText({
          chatId: selectedChatId,
          peerConnectId: activeChat?.peer_connect_id,
          localConnectId: user?.connect_id,
          isGroup,
          isFavorites,
          plaintext: messageText,
        });
        wireText = sealed.body;
        e2eVersion = sealed.e2e_version;
        e2eHeader = sealed.e2e_header;
      } catch (_) {
        if (selectedChatId && !isGroup && !isFavorites) {
          try { await dropSession(selectedChatId); } catch (__) { /* ignore */ }
        }
        showToastNotification('failed to encrypt message', 'error');
        return;
      }
      
      const optimisticMessage = {
        id: tempId,
        message: messageText,
        sender_id: user.user_id,
        chat_id: selectedChatId,
        sender: user.user_name || user.email,
        created_at: new Date().toISOString(),
        client_seq: nextClientSeq(),
        isOptimistic: true,
        encrypted: Boolean(e2eVersion),
        e2e: Boolean(e2eVersion),
      };
      
      setMessages((prev) => [...prev, optimisticMessage]);
      
      if (selectedChatId) {
        flushSync(() => {
          setChats((prevChats) => bumpChatToTop(
            prevChats,
            selectedChatId,
            favoritesChatId,
            optimisticMessage.created_at,
          ));
        });
      }
      
      shouldAutoScrollRef.current = true;
      
      const messagePayload = {
        type: "message",
        message: wireText,
        sender_id: user.user_id,
        chat_id: selectedChatId,
      };
      if (e2eVersion) {
        messagePayload.e2e_version = e2eVersion;
        messagePayload.e2e_header = e2eHeader;
      }
      
      socket.send(JSON.stringify(messagePayload));
    } else {
      showToastNotification('connection error. please try again', 'error', 10000);
    }
    setCurrentMessage('');
  };

  const handleSendVoice = async (blob, durationMs) => {
    if (!selectedChatId || !blob) {
      return;
    }
    let peaks = [];
    try {
      peaks = await extractPeaks(blob);
    } catch (_) {
      peaks = [];
    }
    const tempId = `temp-voice-${Date.now()}-${Math.random()}`;
    const localUrl = URL.createObjectURL(blob);
    const createdAt = new Date().toISOString();
    const optimisticMessage = {
      id: tempId,
      message: '',
      message_type: 'voice_message',
      sender_id: user.user_id,
      chat_id: selectedChatId,
      created_at: createdAt,
      client_seq: nextClientSeq(),
      isOptimistic: true,
      online: 'false',
      attachments: [{
        kind: 'voice',
        url: localUrl,
        duration_ms: durationMs,
        mime: blob.type,
        size: blob.size,
        peaks,
      }],
      meta: { duration_ms: durationMs, peaks },
    };

    setMessages((prev) => [...prev, optimisticMessage]);
    flushSync(() => {
      setChats((prevChats) => bumpChatToTop(
        prevChats,
        selectedChatId,
        favoritesChatId,
        createdAt,
      ));
    });
    applyIncomingPreview(selectedChatId, optimisticMessage);
    shouldAutoScrollRef.current = true;

    try {
      const activeChat = chats.find((c) => String(c.id) === String(selectedChatId));
      const packed = await encryptOutgoingVoice({
        chatId: selectedChatId,
        peerConnectId: activeChat?.peer_connect_id,
        localConnectId: user?.connect_id,
        isGroup: activeChat?.chat_type === 'group',
        isFavorites: activeChat?.peer_name === 'favorites'
          || (favoritesChatId && String(selectedChatId) === String(favoritesChatId)),
        blob,
      });
      const upload = packed.blob;
      const form = new FormData();
      const filename = packed.encrypted
        ? 'voice.e2e'
        : ((blob.type || '').includes('mp4') ? 'voice.m4a' : 'voice.webm');
      form.append('file', upload, filename);
      form.append('duration_ms', String(Math.round(durationMs)));
      if (peaks.length) {
        form.append('peaks', JSON.stringify(peaks));
      }
      if (packed.e2e_version) {
        form.append('e2e_version', String(packed.e2e_version));
        form.append('e2e_header', JSON.stringify(packed.e2e_header));
      }
      const response = await chatAPI.sendVoiceMessage(selectedChatId, form);
      const saved = response.data?.message;
      if (saved) {
        if (saved.id && packed.e2e_header) {
          bindOutboundVoiceMsgId(saved.id, packed.e2e_header);
        }
        handleIncomingMessage({ ...saved, chat_id: selectedChatId });
      }
    } catch (_) {
      let removed = false;
      setMessages((prev) => {
        if (!prev.some((msg) => msg.id === tempId)) {
          return prev;
        }
        removed = true;
        return prev.filter((msg) => msg.id !== tempId);
      });
      if (removed) {
        showToastNotification('could not send voice message', 'error');
      }
    } finally {
      URL.revokeObjectURL(localUrl);
    }
  };

  const validateConnectId = (value) => {
    const digitsOnly = value.replace(/\D/g, '');

    if (digitsOnly.length === 0) {
      return { isValid: false, error: '' };
    }
    if (digitsOnly.length < 9) {
      return { isValid: false, error: 'connect ID must be 9 digits' };
    }
    if (digitsOnly.length > 9) {
      return { isValid: false, error: 'connect ID must be exactly 9 digits' };
    }
    if (!/^\d{9}$/.test(digitsOnly)) {
      return { isValid: false, error: 'connect ID must contain only digits' };
    }
    return { isValid: true, error: '', value: digitsOnly };
  };

  const handleConnectIdChange = (e) => {
    const value = e.target.value;
    const digitsOnly = value.replace(/\D/g, '');
    
    const limitedValue = digitsOnly.slice(0, 9);
    setChatRequestConnectId(limitedValue);
    
    const validation = validateConnectId(limitedValue);
    setChatRequestError(validation.error);
  };

  const handleCreateChatRequest = async () => {
    const validation = validateConnectId(chatRequestConnectId);
    
    if (!validation.isValid) {
      setChatRequestError(validation.error || 'please enter a valid connect ID');
      return;
    }

    try {
      const response = await chatAPI.createChatRequest(validation.value);
      if (response.data.success) {
        setIsChatRequestModalOpen(false);
        setChatRequestConnectId('');
        setChatRequestError('');
        await loadOutgoingRequests();
        showToastNotification(
          'invite sent. if the user exists, they will be able to accept it.',
          'success',
        );
      } else {
        setChatRequestError(response.data.msg || 'failed to send chat invite');
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'failed to send chat invite');
      setChatRequestError(errorMessage);
    }
  };

  const handleCloseChatRequestModal = () => {
    setIsChatRequestModalOpen(false);
    setChatRequestConnectId('');
    setChatRequestError('');
  };

  const handleToggleCreateGroupContact = (connectId) => {
    setSelectedCreateGroupContactIds((prev) => (
      prev.includes(connectId)
        ? prev.filter((id) => id !== connectId)
        : [...prev, connectId]
    ));
    setGroupCreateError('');
  };

  const handleCreateGroup = async () => {
    const trimmedName = groupName.trim();
    const memberConnectIds = selectedCreateGroupContactIds;

    if (!trimmedName) {
      setGroupCreateError('group name is required');
      return;
    }
    if (memberConnectIds.length === 0) {
      setGroupCreateError('select at least one contact');
      return;
    }

    try {
      const response = await chatAPI.createGroupChat({
        name: trimmedName,
        member_connect_ids: memberConnectIds,
      });
      if (response.data.success) {
        setIsCreateGroupModalOpen(false);
        setGroupName('');
        setSelectedCreateGroupContactIds([]);
        setGroupCreateError('');
        showToastNotification(t('notify.groupCreated'), 'success');

        if (response.data.chat_id) {
          setChats((prev) => {
            if (prev.find((chat) => String(chat.id) === String(response.data.chat_id))) {
              return prev;
            }
            return sortChats([
              ...prev,
              {
                id: response.data.chat_id,
                chat_type: 'group',
                title: response.data.title || trimmedName,
                member_count: response.data.member_count || 1,
                last_message_time: null,
              },
            ], favoritesChatId);
          });
          setSelectedChatId(response.data.chat_id);
        }
      } else {
        setGroupCreateError(response.data.msg || 'failed to create group');
      }
    } catch (error) {
      setGroupCreateError(getErrorMessage(error, 'failed to create group'));
    }
  };

  const handleAcceptGroupInvite = async (inviteId) => {
    try {
      const response = await chatAPI.acceptGroupInvite(inviteId);
      if (response.data.success) {
        await loadGroupInvites();
        showToastNotification(t('notify.groupInviteAccepted'), 'success');

        if (response.data.chat_id) {
          setSelectedChatId(response.data.chat_id);
        }
      } else {
        showToastNotification(response.data.msg || 'failed to accept invite', 'error');
      }
    } catch (error) {
      showToastNotification(getErrorMessage(error, 'failed to accept invite'), 'error');
    }
  };

  const handleRejectGroupInvite = async (inviteId) => {
    try {
      const response = await chatAPI.rejectGroupInvite(inviteId);
      if (response.data.success) {
        await loadGroupInvites();
        showToastNotification(t('notify.groupInviteRejected'), 'info');

      } else {
        showToastNotification(response.data.msg || 'failed to reject invite', 'error');
      }
    } catch (error) {
      showToastNotification(getErrorMessage(error, 'failed to reject invite'), 'error');
    }
  };

  const handleStartVoiceCall = useCallback(async () => {
    if (!selectedChatId) {
      return;
    }
    const chat = chats.find((item) => String(item.id) === String(selectedChatId));
    if (!chat || chat.peer_name === 'favorites') {
      return;
    }
    if (isInCall) {
      showToastNotification(t('notify.finishCallFirst'), 'error');
      return;
    }
    if (chat.chat_type === 'group') {
      try {
        const room = activeGroupRooms[String(chat.id)];
        if (room?.room_id) {
          await joinGroupRoom(room.room_id);
        } else {
          await startGroupRoom(selectedChatId);
        }
      } catch (error) {
        showToastNotification(getErrorMessage(error, 'failed to join group voice'), 'error');
      }
      return;
    }
    if (chat.user_online !== 'true') {
      showToastNotification(t('notify.peerOffline'), 'error');
    }
    try {
      await startCall(selectedChatId);
    } catch (error) {
      showToastNotification(getErrorMessage(error, 'failed to start call'), 'error');
    }
  }, [
    activeGroupRooms,
    chats,
    isInCall,
    joinGroupRoom,
    selectedChatId,
    showToastNotification,
    startCall,
    startGroupRoom,
    t,
  ]);

  useEffect(() => {
    if (!selectedChatId) {
      return undefined;
    }
    const chat = chats.find((item) => String(item.id) === String(selectedChatId));
    if (!chat || chat.chat_type !== 'group') {
      return undefined;
    }
    let cancelled = false;
    callAPI.getActiveRoom(selectedChatId)
      .then((response) => {
        if (cancelled) {
          return;
        }
        const room = response.data?.room;
        if (room) {
          setActiveGroupRooms((prev) => ({
            ...prev,
            [room.chat_id]: room,
          }));
        } else {
          setActiveGroupRooms((prev) => {
            if (!prev[selectedChatId]) {
              return prev;
            }
            const next = { ...prev };
            delete next[selectedChatId];
            return next;
          });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [chats, selectedChatId, setActiveGroupRooms]);

  const handleOpenGroupInfo = async (chatId) => {
    const targetChatId = chatId ?? selectedChatId;
    const chat = chats.find((item) => String(item.id) === String(targetChatId));
    if (!targetChatId || chat?.chat_type !== 'group') {
      return;
    }
    setOpenChatMenuId(null);
    // Seed title from sidebar immediately so rename field is never a generic fallback.
    setGroupInfo((prev) => ({
      ...(prev && String(prev.id) === String(targetChatId) ? prev : {}),
      id: targetChatId,
      title: chat.title || prev?.title || '',
      is_creator: Boolean(chat.is_creator),
    }));
    if (String(selectedChatId) !== String(targetChatId)) {
      handleSelectChat(targetChatId);
    }
    try {
      await loadGroupMembers(targetChatId);
      setIsGroupInfoModalOpen(true);
    } catch (error) {
      showToastNotification(getErrorMessage(error, 'failed to load group info'), 'error');
    }
  };

  const handleDeleteGroupFromInfo = () => {
    const chat = chats.find((item) => String(item.id) === String(selectedChatId));
    if (!chat) {
      return;
    }
    setIsGroupInfoModalOpen(false);
    setChatToDelete({ ...chat, is_creator: isGroupCreator });
    setIsDeleteChatModalOpen(true);
  };

  const handleAddGroupMembers = async (memberConnectIds) => {
    if (!selectedChatId) {
      throw new Error('no group selected');
    }
    const response = await chatAPI.addGroupMembers(selectedChatId, memberConnectIds);
    if (!response.data.success) {
      throw new Error(response.data.msg || 'failed to invite members');
    }
    await loadGroupMembers(selectedChatId);
    showToastNotification(t('notify.membersInvited'), 'success');
  };

  const handleRenameGroup = async (newName) => {
    if (!selectedChatId) {
      throw new Error('no group selected');
    }
    try {
      const response = await chatAPI.renameGroupChat(selectedChatId, newName);
      if (!response.data.success) {
        throw new Error(response.data.msg || 'failed to rename group');
      }
      const title = response.data.title || newName;
      setChats((prev) => prev.map((chat) => (
        String(chat.id) === String(selectedChatId) ? { ...chat, title } : chat
      )));
      setGroupInfo((prev) => (prev ? { ...prev, title } : prev));
      showToastNotification(t('notify.groupRenamed'), 'success');
    } catch (error) {
      throw new Error(getErrorMessage(error, 'failed to rename group'));
    }
  };

  const handleCancelGroupInvite = async (inviteId) => {
    if (!inviteId) {
      return;
    }
    try {
      const response = await chatAPI.cancelGroupInvite(inviteId);
      if (response.data.success) {
        if (selectedChatId) {
          await loadGroupMembers(selectedChatId);
        }
        showToastNotification('invite cancelled', 'success');
      } else {
        showToastNotification(response.data.msg || 'failed to cancel invite', 'error');
      }
    } catch (error) {
      showToastNotification(getErrorMessage(error, 'failed to cancel invite'), 'error');
    }
  };

  const handleRemoveGroupMember = async (connectId) => {
    if (!selectedChatId || !connectId) {
      return;
    }
    try {
      const response = await chatAPI.removeGroupMember(selectedChatId, connectId);
      if (response.data.success) {
        await loadGroupMembers(selectedChatId);
        showToastNotification('member removed', 'success');
      } else {
        showToastNotification(response.data.msg || 'failed to remove member', 'error');
      }
    } catch (error) {
      showToastNotification(getErrorMessage(error, 'failed to remove member'), 'error');
    }
  };

  const handleLeaveGroup = async () => {
    if (!selectedChatId || !user?.connect_id) {
      return;
    }
    try {
      const response = await chatAPI.removeGroupMember(selectedChatId, user.connect_id);
      if (response.data.success) {
        setIsGroupInfoModalOpen(false);
        setChats((prev) => prev.filter((chat) => String(chat.id) !== String(selectedChatId)));
        setSelectedChatId(null);
        setMessages([]);
        showToastNotification(t('notify.leftGroup'), 'info');
      } else {
        showToastNotification(response.data.msg || 'failed to leave group', 'error');
      }
    } catch (error) {
      showToastNotification(getErrorMessage(error, 'failed to leave group'), 'error');
    }
  };

  const handleCloseCreateGroupModal = () => {
    setIsCreateGroupModalOpen(false);
    setGroupName('');
    setSelectedCreateGroupContactIds([]);
    setGroupCreateError('');
  };

  useOutsideClick(chatMenuRef, () => setIsChatMenuOpen(false));

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openChatMenuId) {
        const menuRef = chatItemMenuRefs.current[openChatMenuId];
        const portalMenu = document.querySelector('[data-portal-chat-menu]');
        const clickedInsideMenu = menuRef && menuRef.contains(event.target);
        const clickedInsidePortal = portalMenu && portalMenu.contains(event.target);
        
        if (!clickedInsideMenu && !clickedInsidePortal) {
          setOpenChatMenuId(null);
        }
      }
    };

    if (openChatMenuId) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [openChatMenuId]);

  const handleOpenChatMenu = (e) => {
    e.stopPropagation();
    setIsChatMenuOpen(!isChatMenuOpen);
  };

  const handleMenuSendRequest = () => {
    setIsChatMenuOpen(false);
    setIsChatRequestModalOpen(true);
  };

  const handleMenuCreateGroup = () => {
    setIsChatMenuOpen(false);
    setIsCreateGroupModalOpen(true);
  };

  const handleMenuGroupInvites = async () => {
    setIsChatMenuOpen(false);
    setIsGroupInvitesModalOpen(true);
    await loadGroupInvites();
  };

  const handleMenuChatRequests = async (tab = 'incoming') => {
    setIsChatMenuOpen(false);
    setChatRequestsTab(tab);
    setIsChatRequestsModalOpen(true);
    await Promise.all([loadIncomingRequests(), loadOutgoingRequests()]);
  };

  const handleDeleteChatClick = (e, chat) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenChatMenuId(null);
    setChatToDelete(chat);
    setIsDeleteChatModalOpen(true);
  };

  const handleOpenChatItemMenu = (e, chatId) => {
    e.preventDefault();
    e.stopPropagation();
    setOpenChatMenuId(openChatMenuId === chatId ? null : chatId);
  };

  const handleDeleteMessageClick = (messageId) => {
    setMessageToDelete(messageId);
    setIsDeleteMessageModalOpen(true);
  };

  const handleConfirmDeleteMessage = async () => {
    if (!selectedChatId || !messageToDelete) return;

    setIsDeleteMessageModalOpen(false);
    const messageId = messageToDelete;
    setMessageToDelete(null);

    try {
      // Optimistically remove message from UI
      setMessages(prev => prev.filter(msg => msg.id !== messageId));
      
      await chatAPI.deleteMessage(selectedChatId, messageId);
      showToastNotification(t('notify.messageDeleted'), 'success', 5000);
    } catch (error) {
      console.error('Failed to delete message:', error);
      // Reload messages to restore correct state
      if (selectedChatId) {
        const reloadedMessages = await fetchMessages(selectedChatId, 0);
        setMessages(reloadedMessages || []);
      }
      const errorMessage = getErrorMessage(error, 'failed to delete message');
      showToastNotification(errorMessage, 'error', 10000);
    }
  };

  const handleCloseDeleteMessageModal = () => {
    setIsDeleteMessageModalOpen(false);
    setMessageToDelete(null);
  };

  const handleDeleteChat = async (forEveryone) => {
    if (!chatToDelete) return;

    const deletedChatId = chatToDelete.id;
    const wasSelectedChat = deletedChatId === selectedChatId;

    stopChatBlink(deletedChatId);
    clearChatBlink(deletedChatId);
    setUnreadChatIds((prev) => prev.filter((item) => String(item) !== String(deletedChatId)));

    setChats(prev => prev.filter(chat => chat.id !== deletedChatId));
    
    setIsDeleteChatModalOpen(false);
    setChatToDelete(null);
    
    if (wasSelectedChat) {
      if (favoritesChatId) {
        setSelectedChatId(favoritesChatId);
      } else {
        setSelectedChatId(null);
      }
      setMessages([]);
    }

    try {
      const response = await chatAPI.deleteChat(deletedChatId, forEveryone);
      if (response.data.success) {
        showToastNotification(response.data.msg || t('notify.chatDeleted'), 'success');
      } else {
        // If failed, the chats_list will be updated via WebSocket to restore correct state
        showToastNotification(response.data.msg || 'error deleting chat', 'error');
      }
    } catch (error) {
      // If failed, the chats_list will be updated via WebSocket to restore correct state
      const errorMessage = getErrorMessage(error, 'error deleting chat');
      showToastNotification(errorMessage, 'error');
    }
  };

  const handleCloseDeleteChatModal = () => {
    setIsDeleteChatModalOpen(false);
    setChatToDelete(null);
  };

  const onTrustPeer = async (e, chat) => {
    e.preventDefault();
    e.stopPropagation();
    if (chat?.chat_type === 'group') {
      return;
    }
    setChatToTrust(chat);
    
    // Load trust settings from backend before opening modal
    // This ensures current values are displayed when modal opens
    try {
      const response = await chatAPI.getTrustSettings(chat.id, false); // false = get current user's settings for peer (for editing)
      
      // Handle both response formats: {success: true, settings: {...}} or direct {share_avatar, share_email, share_status}
      let settings = null;
      if (response.data.success && response.data.settings) {
        // New format with wrapper
        settings = response.data.settings;
      } else if (response.data.share_avatar !== undefined || response.data.share_email !== undefined || response.data.share_status !== undefined) {
        // Direct format without wrapper
        settings = response.data;
      }
      
      if (settings) {
        setTrustSettings({
          shareAvatar: settings.share_avatar || false,
          shareEmail: settings.share_email || false,
          shareStatus: settings.share_status || false
        });
      } else {
        // Default: all false if no settings found
        setTrustSettings({
          shareAvatar: false,
          shareEmail: false,
          shareStatus: false
        });
      }
    } catch (error) {
      console.error('Failed to load trust settings:', error);
      // Use default settings if loading fails
      setTrustSettings({
        shareAvatar: false,
        shareEmail: false,
        shareStatus: false
      });
    }
    
    setIsTrustPeerModalOpen(true);
    setOpenChatMenuId(null);
  };

  const handleCloseTrustPeerModal = () => {
    setIsTrustPeerModalOpen(false);
    setChatToTrust(null);
  };

  const handleTrustSettingChange = (setting) => {
    setTrustSettings(prev => {
      const newSettings = { ...prev, [setting]: !prev[setting] };
      return newSettings;
    });
  };

  const handleSelectAllTrust = () => {
    const allSelected = trustSettings.shareAvatar && trustSettings.shareEmail && trustSettings.shareStatus;
    const newSettings = {
      shareAvatar: !allSelected,
      shareEmail: !allSelected,
      shareStatus: !allSelected
    };
    setTrustSettings(newSettings);
  };

  const handleSaveTrustSettings = async () => {
    if (!chatToTrust) return;

    try {
      const response = await chatAPI.updateTrustSettings(chatToTrust.id, {
        share_avatar: trustSettings.shareAvatar,
        share_email: trustSettings.shareEmail,
        share_status: trustSettings.shareStatus
      });
      if (response.data.success) {
        // Update trustSettings state with saved values to reflect what was actually saved
        // This ensures the modal shows correct values if reopened
        setTrustSettings({
          shareAvatar: response.data.settings.share_avatar || false,
          shareEmail: response.data.settings.share_email || false,
          shareStatus: response.data.settings.share_status || false
        });
        
        setIsTrustPeerModalOpen(false);
        setChatToTrust(null);
        showToastNotification(t('notify.trustSaved'), 'success');
        
        // Reload trust policies and chats list to reflect changes
        // When we change our trust settings, it affects what the peer sees
        // We need to reload the peer's trust policies (what they share with us) 
        // and also reload chats list to see updated status
        if (chatToTrust) {
          // Force reload of trust policies for all chats to ensure UI is updated
          // This will update the display of status and avatars
          const reloadAllTrustPolicies = async () => {
            if (!chats || chats.length === 0) return;
            
            const policies = {};
            for (const chat of chats) {
              // Skip favorites chats
              if (chat.peer_name === 'favorites') continue;
              
              try {
                const response = await chatAPI.getTrustSettings(chat.id, true);
                
                // Handle both response formats: {success: true, settings: {...}} or direct {share_avatar, share_email, share_status}
                let settings = null;
                if (response.data.success && response.data.settings) {
                  // New format with wrapper
                  settings = response.data.settings;
                } else if (response.data.share_avatar !== undefined || response.data.share_email !== undefined || response.data.share_status !== undefined) {
                  // Direct format without wrapper
                  settings = response.data;
                }
                
                if (settings) {
                  policies[chat.id] = {
                    shareAvatar: Boolean(settings.share_avatar),
                    shareEmail: Boolean(settings.share_email),
                    shareStatus: Boolean(settings.share_status)
                  };
                } else {
                  policies[chat.id] = {
                    shareAvatar: false,
                    shareEmail: false,
                    shareStatus: false
                  };
                }
              } catch (error) {
                console.error(`[Trust Policy Reload] Failed to load trust policy for chat ${chat.id}:`, error);
                policies[chat.id] = {
                  shareAvatar: false,
                  shareEmail: false,
                  shareStatus: false
                };
              }
            }
            // Use functional update to merge with existing policies instead of overwriting
            // This prevents race conditions where multiple loads might overwrite correct values
            setChatTrustPolicies(prev => {
              const merged = { ...prev, ...policies };
              
              // After trust policies are updated, force reload avatars for all chats
              // Clear avatar caches for chats where trust policy changed
              for (const chat of chats) {
                if (chat.peer_name === 'favorites') continue;
                
                const chatId = chat.id;
                const peerConnectId = chat.peer_connect_id;
                if (!peerConnectId) continue;
                
                const oldPolicy = prev[chatId];
                const newPolicy = merged[chatId];
                
                // If trust policy changed for avatar sharing, clear avatar cache
                if (oldPolicy?.shareAvatar !== newPolicy?.shareAvatar) {
                  // Clear loading and no-avatar flags
                  loadedAvatarsRef.current.delete(peerConnectId);
                  noAvatarRef.current.delete(peerConnectId);
                  
                  // Clear avatar from state if policy changed
                  setPeerAvatars(prevAvatars => {
                    const updated = { ...prevAvatars };
                    if (updated[peerConnectId]) {
                      // Revoke blob URL if exists
                      if (updated[peerConnectId].startsWith('blob:')) {
                        try {
                          URL.revokeObjectURL(updated[peerConnectId]);
                        } catch (e) {
                          // Ignore errors if URL was already revoked
                        }
                      }
                      delete updated[peerConnectId];
                      return updated;
                    }
                    return prevAvatars;
                  });
                }
              }
              
              return merged;
            });
            
            // Force reload avatars after trust policies are updated
            // Use setTimeout to ensure state updates are complete
            setTimeout(() => {
              // Trigger avatar reload by clearing and reloading
              // The useEffect will handle the actual loading
              setPeerAvatars(prev => {
                // Force re-render by creating a new object reference
                return { ...prev };
              });
            }, 100);
          };
          
          // Reload all trust policies once
          // Use functional update to merge with existing policies instead of overwriting
          reloadAllTrustPolicies();
        }
      }
    } catch (error) {
      console.error('Failed to save trust settings:', error);
      showToastNotification('error saving trust settings', 'error');
    }
  };

  const handleAcceptRequest = async (fromUserConnectId) => {
    try {
      removeIncomingRequest(fromUserConnectId);

      const response = await chatAPI.acceptChatRequest(fromUserConnectId);
      if (response.data.success) {
        // Reload to ensure consistency with server
        await loadIncomingRequests();
        // Список чатов обновится автоматически через WebSocket (chats_list или chat_created)
        // Не нужно делать HTTP запрос, так как эндпоинта /api/v1/chats не существует
      } else {
        // If failed, reload to restore correct state
        await loadIncomingRequests();
        console.error('Accept request failed:', response.data.msg);
        showToastNotification(response.data.msg || 'failed to accept request', 'error');
      }
    } catch (error) {
      // If failed, reload to restore correct state
      await loadIncomingRequests();
      console.error('Failed to accept request:', error);
      const errorMessage = getErrorMessage(error, 'failed to accept request');
      showToastNotification(errorMessage, 'error');
    }
  };

  const handleRejectRequest = async (fromUserConnectId) => {
    try {
      removeIncomingRequest(fromUserConnectId);

      const response = await chatAPI.rejectChatRequest(fromUserConnectId);
      if (response.data.success) {
        // Reload to ensure consistency with server
        await loadIncomingRequests();
      } else {
        // If failed, reload to restore correct state
        await loadIncomingRequests();
        console.error('Reject request failed:', response.data.msg);
        showToastNotification(response.data.msg || 'failed to reject request', 'error');
      }
    } catch (error) {
      // If failed, reload to restore correct state
      await loadIncomingRequests();
      console.error('Failed to reject request:', error);
      const errorMessage = getErrorMessage(error, 'failed to reject request');
      showToastNotification(errorMessage, 'error');
    }
  };

  // Флаг для предотвращения повторных вызовов при быстрых кликах
  const isSelectingChatRef = useRef(false);
  
  const handleSelectChat = async (chatId) => {
    // Предотвращаем повторные вызовы
    if (isSelectingChatRef.current) return;
    if (chatId === selectedChatId) {
      markChatRead(chatId);
      if (isMobile) {
        setIsSidebarOpen(false);
      }
      return;
    }
    
    // Устанавливаем флаг блокировки
    isSelectingChatRef.current = true;

    // Загружаем аватар для выбранного чата, если его еще нет
    // Пропускаем чаты "favorites", так как у них дефолтные аватары
    const selectedChat = chats.find(c => c.id === chatId);
    const peerConnectId = selectedChat?.peer_connect_id;
    
    if (peerConnectId && 
        peerConnectId !== user?.connect_id &&
        selectedChat.peer_name !== 'favorites') {
      // Check trust policy: does peer share avatar with me?
      const trustPolicy = chatTrustPolicies[chatId];
      if (trustPolicy && !trustPolicy.shareAvatar) {
        // Peer doesn't share avatar - use default avatar
        setPeerAvatars(prev => {
          if (prev[peerConnectId] !== null) {
            return { ...prev, [peerConnectId]: null };
          }
          return prev;
        });
        noAvatarRef.current.add(peerConnectId);
      } else {
      // Check if we already have the avatar loaded (including null for default avatar)
      const hasAvatarInfo = peerAvatars.hasOwnProperty(peerConnectId);
      // Check if we already know this user doesn't have an avatar
      const hasNoAvatar = noAvatarRef.current.has(peerConnectId);
      // Check if we're already loading this avatar
      const isLoading = loadedAvatarsRef.current.has(peerConnectId);
      
      // Only make request if we don't have avatar info, don't know user has no avatar, and not loading
      if (!hasAvatarInfo && !hasNoAvatar && !isLoading) {
        // Mark as loading and make request
        loadedAvatarsRef.current.add(peerConnectId);
        profileAPI.getAvatar(peerConnectId)
        .then(avatarResponse => {
            // Check content-type to determine if response is JSON or blob
            const contentType = avatarResponse.headers['content-type'] || '';
            if (contentType.startsWith('application/json')) {
              // JSON response means "use default avatar" - parse from arraybuffer
              const decoder = new TextDecoder();
              const jsonText = decoder.decode(avatarResponse.data);
              const jsonData = JSON.parse(jsonText);
              if (jsonData.message === 'use default avatar') {
                noAvatarRef.current.add(peerConnectId);
                setPeerAvatars(prev => ({ ...prev, [peerConnectId]: null }));
              }
            } else if (contentType.startsWith('image/')) {
              // Image blob response - convert arraybuffer to blob, then to object URL
              // Проверяем, не создан ли уже blob URL для этого аватара
              const existingUrl = peerAvatarsRef.current[peerConnectId];
              if (existingUrl && existingUrl.startsWith('blob:')) {
                // Если blob URL уже существует, используем его (не создаем новый)
                return;
              }
              const avatarBlob = new Blob([avatarResponse.data], { type: contentType });
              const avatarObjectUrl = URL.createObjectURL(avatarBlob);
              setPeerAvatars(prev => ({ ...prev, [peerConnectId]: avatarObjectUrl }));
            }
        })
        .catch(error => {
            if (error?.response?.status === 404) {
              noAvatarRef.current.add(peerConnectId);
              setPeerAvatars(prev => ({ ...prev, [peerConnectId]: null }));
            } else {
              console.error('Error loading avatar:', error);
              loadedAvatarsRef.current.delete(peerConnectId);
            }
        });
        }
      }
    }

    // Используем queueMicrotask для выполнения после завершения рендеринга
    queueMicrotask(() => {
        flushSync(() => {
        markChatRead(chatId);
        clearChatBlink(chatId);

        // Устанавливаем новый выбранный чат
        // useEffect автоматически загрузит сообщения при изменении selectedChatId
        setSelectedChatId(chatId);
        
        // Очищаем сообщения и сбрасываем offset
        setMessages([]);
        setCallHistory([]);
        setOffset(0);
      });
    });
    
    // Очищаем отслеживание обработанных сообщений при смене чата
    processedMessageIdsRef.current.clear();
    
    // Очищаем неиспользуемые аватары (оптимизация памяти)
    // Оставляем только аватары для текущих чатов и активного чата
    // Делаем это с задержкой, чтобы не очистить blob URL до загрузки изображения
    setTimeout(() => {
      setPeerAvatars(prev => {
      const activeConnectIds = new Set(
        chats
            .map(c => c.peer_connect_id)
          .filter(id => id && id !== user?.connect_id)
      );
      // Добавляем connect_id активного чата
        if (selectedChat?.peer_connect_id) {
          activeConnectIds.add(selectedChat.peer_connect_id);
      }
      
      const cleaned = {};
      Object.entries(prev).forEach(([connectId, url]) => {
        if (activeConnectIds.has(connectId)) {
          cleaned[connectId] = url;
        } else {
          // Освобождаем память для неиспользуемых аватаров
            try {
          URL.revokeObjectURL(url);
            } catch (e) {
              // Ignore errors if URL was already revoked
            }
          loadedAvatarsRef.current.delete(connectId);
        }
      });
      return cleaned;
    });
    }, 1000); // Delay to ensure images are loaded
  
    // Снимаем блокировку после завершения
    isSelectingChatRef.current = false;
  };

  const handleSelectChatRef = useRef(handleSelectChat);
  handleSelectChatRef.current = handleSelectChat;

  useEffect(() => {
    const onOpenChat = (event) => {
      const chatId = event.detail?.chatId;
      if (chatId) {
        handleSelectChatRef.current(chatId);
      }
    };

    window.addEventListener(OPEN_CHAT_EVENT, onOpenChat);
    return () => window.removeEventListener(OPEN_CHAT_EVENT, onOpenChat);
  }, []);

  useEffect(() => {
    if (!chats.length) {
      return;
    }
    const pendingChatId = consumePendingOpenChatId();
    if (!pendingChatId) {
      return;
    }
    const chatExists = chats.some((chat) => String(chat.id) === pendingChatId);
    if (chatExists) {
      handleSelectChatRef.current(pendingChatId);
    }
  }, [chats]);

  useLayoutEffect(() => {
    let isMounted = true;

    if (!selectedChatId) {
      setIsChatContentReady(false);
      setMessages([]);
      setCallHistory([]);
      return undefined;
    }

    const chatIdForLoad = selectedChatId;
    const epoch = chatLoadEpochRef.current + 1;
    chatLoadEpochRef.current = epoch;
    // Keep ref in sync inside layout effect — useEffect runs too late for stillCurrent.
    selectedChatIdRef.current = selectedChatId;
    shouldAutoScrollRef.current = true;
    pendingChatRevealRef.current = false;
    loadingOlderRef.current = false;
    setIsChatContentReady(false);
    setMessages([]);
    setCallHistory([]);
    setOffset(0);

    const selectedChat = chatsRef.current.find(
      (chat) => String(chat.id) === String(selectedChatId),
    );
    if (selectedChat?.chat_type === 'group') {
      loadGroupMembers(selectedChatId).catch(() => {});
    }

    const stillCurrent = () => (
      isMounted
      && epoch === chatLoadEpochRef.current
      && String(selectedChatIdRef.current) === String(chatIdForLoad)
    );

    const loadSelectedChat = async () => {
      try {
        if (callHistoryAbortRef.current) {
          callHistoryAbortRef.current.abort();
        }
        const controller = new AbortController();
        callHistoryAbortRef.current = controller;

        // Load call history first so the pane is never blank while E2E decrypt runs.
        const callItems = await fetchCallHistory(chatIdForLoad, controller.signal);
        if (!stillCurrent()) {
          return;
        }
        if (callHistoryAbortRef.current === controller) {
          callHistoryAbortRef.current = null;
        }
        if (callItems !== null) {
          setCallHistory(callItems || []);
          setIsChatContentReady(true);
        }

        let messagesForChat = [];
        try {
          messagesForChat = (await fetchMessages(chatIdForLoad, 0)) || [];
        } catch (_) {
          messagesForChat = [];
        }
        if (!stillCurrent()) {
          return;
        }

        shouldAutoScrollRef.current = true;
        flushSync(() => {
          if (!stillCurrent()) {
            return;
          }
          setMessages((prevLive) => {
            const merged = mergeMessageListsPreferPlain(
              messagesForChat,
              prevLive || [],
              chatIdForLoad,
            );
            merged.forEach((msg) => {
              const nid = normalizeMessageId(msg.id);
              if (nid) {
                processedMessageIdsRef.current.add(nid);
              }
            });
            return merged;
          });
          setIsChatContentReady(true);
        });
        pendingChatRevealRef.current = false;
        queueMicrotask(() => {
          if (stillCurrent()) {
            scrollMessagesToBottom();
          }
        });
      } catch (error) {
        if (stillCurrent()) {
          pendingChatRevealRef.current = false;
          flushSync(() => {
            setIsChatContentReady(true);
          });
        }
      }
    };

    loadSelectedChat();

    return () => {
      isMounted = false;
      if (callHistoryAbortRef.current) {
        callHistoryAbortRef.current.abort();
        callHistoryAbortRef.current = null;
      }
    };
    // Intentionally only selectedChatId: other callback identity changes were
    // re-fetching history and wiping the sender's outbound plaintext with 🔒.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChatId]);

  // Scroll to bottom before paint, then reveal — no visible jump.
  useLayoutEffect(() => {
    if (pendingChatRevealRef.current) {
      scrollMessagesToBottom();
      pendingChatRevealRef.current = false;
      shouldAutoScrollRef.current = true;
      setIsChatContentReady(true);
      return;
    }

    if (loadingOlderRef.current) {
      return;
    }

    if (isChatContentReady && shouldAutoScrollRef.current) {
      scrollMessagesToBottom();
    }
  }, [messages, callHistory, isChatContentReady, scrollMessagesToBottom]);

  if (!user) {
    // Don't show anything while loading to avoid visual jumps
    // The page will appear once user data is loaded
    return null;
  }

  const selectedChat = selectedChatId
    ? chats.find((chat) => String(chat.id) === String(selectedChatId))
    : null;

  const canUseVoiceCalls = selectedChat
    && selectedChat.peer_name !== 'favorites';
  const isGroupChat = selectedChat?.chat_type === 'group';
  const activeGroupRoom = isGroupChat
    ? activeGroupRooms[String(selectedChatId)]
    : null;
  const inThisGroupRoom = Boolean(
    isInCall
    && activeCall?.kind === 'group'
    && activeGroupRoom
    && String(activeCall.call_id) === String(activeGroupRoom.room_id),
  );
  const voiceButtonLabel = (() => {
    if (isInCall) {
      return inThisGroupRoom || !isGroupChat ? t('workspace.endCall') : t('workspace.busyInCall');
    }
    if (isGroupChat) {
      return activeGroupRoom ? t('workspace.joinVoiceRoom') : t('workspace.startVoiceRoom');
    }
    return t('workspace.voiceCall');
  })();

  return (
    <WorkspaceContainer>
      <GlobalStyle />
      {user && (
        <>
          <MenuButton onClick={toggleMenu} $open={menuOpen}>
            {user.email.slice(0, 2).toUpperCase() || '≡'}
          </MenuButton>
          <DropdownMenu ref={dropdownRef} open={menuOpen}>
            <DropdownItem to="/">{t('nav.home')}</DropdownItem>
            <DropdownItem to="/profile">{t('nav.profile')}</DropdownItem>
            {user.is_admin && <DropdownItem to="/admin">{t('nav.adminPanel')}</DropdownItem>}
            <ThemeToggle />
            <LanguageToggle />
            <LogoutButton onClick={handleLogout}>{t('nav.logout')}</LogoutButton>
          </DropdownMenu>
        </>
      )}
      <div 
        style={{ 
          display: 'flex', 
          alignItems: 'stretch', 
          width: '100%',
          height: '100%',
          flex: 1,
          overflow: 'hidden',
          position: 'relative'
        }}
        data-workspace-container
      >
        <SidebarWrapper isOpen={isSidebarOpen} width={sidebarWidth} isResizing={isResizing} data-sidebar-wrapper>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            width: '100%',
            height: '100%',
            gap: '0',
            flexShrink: 0
          }}>
            <ChatSidebar
              chats={chats}
              onSelectChat={handleSelectChat}
              selectedChatId={selectedChatId}
              isServiceConnected={true}
              blinkingChats={blinkingChats}
              unreadChatIds={unreadChatIds}
              incomingRequestsCount={incomingCount}
              isCollapsed={isChatsCollapsed}
              onToggleCollapse={() => setIsChatsCollapsed(!isChatsCollapsed)}
              fillRemaining={true}
              onAddChatClick={handleMenuSendRequest}
              chatMenuRef={chatMenuRef}
              isChatMenuOpen={isChatMenuOpen}
              onOpenChatMenu={handleOpenChatMenu}
              onMenuSendRequest={handleMenuSendRequest}
              onMenuCreateGroup={handleMenuCreateGroup}
              onMenuChatRequests={() => handleMenuChatRequests('incoming')}
              onMenuGroupInvites={handleMenuGroupInvites}
              groupInvitesCount={groupInvitesCount}
              onDeleteChat={handleDeleteChatClick}
              onTrustPeer={onTrustPeer}
              onOpenGroupInfo={handleOpenGroupInfo}
              openChatMenuId={openChatMenuId}
              onOpenChatItemMenu={handleOpenChatItemMenu}
              chatItemMenuRefs={chatItemMenuRefs}
              chatTrustPolicies={chatTrustPolicies}
              isLoadingChats={isLoadingChats}
              user={user}
              userAvatarUrl={userAvatarUrl}
              peerAvatars={peerAvatars}
            />
          </div>
        </SidebarWrapper>
        <Resizer
          ref={resizerRef}
          isOpen={isSidebarOpen}
          isResizing={isResizing}
          onMouseDown={(e) => {
            e.preventDefault();
            setIsResizing(true);
          }}
        />
        <ChatContainer $mobileHidden={isMobile && isSidebarOpen}>
          <ChatTopBar>
            {isMobile ? (
              <SidebarToggleButton
                isOpen
                onClick={handleMobileBackToChats}
                $inTopBar
                aria-label={t('workspace.backToChats')}
                title={t('workspace.backToChats')}
              >
                <HiOutlineChevronLeft />
              </SidebarToggleButton>
            ) : (
              <SidebarToggleButton
                isOpen={isSidebarOpen}
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                $inTopBar
              >
                {isSidebarOpen ? <HiOutlineChevronLeft /> : <HiOutlineChevronRight />}
              </SidebarToggleButton>
            )}
            {canUseVoiceCalls && (
              <CallHeaderButton
                type="button"
                style={
                  isInCall && (!isGroupChat || inThisGroupRoom)
                    ? { color: techColors.danger }
                    : undefined
                }
                onClick={() => {
                  if (isInCall && isGroupChat && !inThisGroupRoom) {
                    showToastNotification(t('notify.finishCallFirst'), 'error');
                    return;
                  }
                  if (isInCall) {
                    endCall();
                    return;
                  }
                  handleStartVoiceCall();
                }}
                aria-label={voiceButtonLabel}
                aria-disabled={isInCall && isGroupChat && !inThisGroupRoom}
                disabled={isInCall && isGroupChat && !inThisGroupRoom}
                title={voiceButtonLabel}
              >
                {isInCall && (!isGroupChat || inThisGroupRoom)
                  ? <HiPhoneXMark />
                  : <HiOutlinePhone />}
              </CallHeaderButton>
            )}
            {selectedChat ? (
              <ChatHeader
                chat={selectedChat}
                chatTrustPolicies={chatTrustPolicies}
                embedded
                onOpenMembers={selectedChat.chat_type === 'group' ? () => handleOpenGroupInfo(selectedChat.id) : undefined}
              />
            ) : (
              <ChatTopBarPlaceholder>{t('workspace.selectChat')}</ChatTopBarPlaceholder>
            )}
          </ChatTopBar>
          <ChatPane>
          {selectedChatId ? (
            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {activeGroupRoom && !inThisGroupRoom && (
                <VoiceRoomBannerWrap>
                  <VoiceRoomBanner>
                    <VoiceRoomLiveDot />
                    <VoiceRoomBannerLabel>
                      {t('workspace.voiceRoomInCall', { count: (activeGroupRoom.participants || []).length })}
                    </VoiceRoomBannerLabel>
                    <VoiceRoomJoinButton
                      type="button"
                      onClick={() => handleStartVoiceCall()}
                    >
                      {t('workspace.join')}
                    </VoiceRoomJoinButton>
                  </VoiceRoomBanner>
                </VoiceRoomBannerWrap>
              )}
              <MessageList
                key={selectedChatId}
                messages={messages}
                callHistory={callHistory}
                user={user}
                chats={chats}
                selectedChatId={selectedChatId}
                userAvatarUrl={userAvatarUrl}
                peerAvatars={peerAvatars}
                chatTrustPolicies={chatTrustPolicies}
                messagesContainerRef={messagesContainerRef}
                messagesEndRef={messagesEndRef}
                onScroll={handleScroll}
                isLoading={isLoading}
                onDeleteMessage={handleDeleteMessageClick}
                groupSenderNames={groupSenderNames}
              />
            </div>
          ) : (
            <EmptyChatState>
              <strong>{t('workspace.selectChat')}</strong>
              <span>{t('workspace.selectChatHint')}</span>
            </EmptyChatState>
          )}
        {selectedChatId && (
          <ChatInputContainer>
            <InputAndButtonsContainer>
              <VoiceComposer
                chatId={selectedChatId}
                onSend={handleSendVoice}
                onError={(message) => showToastNotification(message, 'error')}
              >
                <ChatMessageInput
                  value={currentMessage}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    if (newValue.length <= MAX_MESSAGE_LENGTH) {
                      setCurrentMessage(newValue);
                    }
                  }}
                  maxLength={MAX_MESSAGE_LENGTH}
                  placeholder={currentMessage.length >= MAX_MESSAGE_LENGTH ? t('workspace.maxCharacters', { count: MAX_MESSAGE_LENGTH }) : t('workspace.typeMessage')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      if (e.shiftKey) {
                        e.preventDefault();
                        if (currentMessage.length < MAX_MESSAGE_LENGTH) {
                          setCurrentMessage((prev) => `${prev}\n`);
                        }
                      } else {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }
                  }}
                />
                <SendButton onClick={handleSendMessage}>
                  <HiOutlineArrowRight />
                </SendButton>
              </VoiceComposer>
            </InputAndButtonsContainer>
          </ChatInputContainer>
        )}
          </ChatPane>
      </ChatContainer>
      </div>
      <ChatRequestModal
        isOpen={isChatRequestModalOpen}
        onClose={handleCloseChatRequestModal}
        connectId={chatRequestConnectId}
        error={chatRequestError}
        onConnectIdChange={handleConnectIdChange}
        onCreateRequest={handleCreateChatRequest}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleCreateChatRequest();
          } else if (e.key === 'Escape') {
            handleCloseChatRequestModal();
          }
        }}
      />
      <ChatRequestsListModal
        isOpen={isChatRequestsModalOpen}
        onClose={() => setIsChatRequestsModalOpen(false)}
        initialTab={chatRequestsTab}
        incomingRequests={incomingRequests}
        outgoingRequests={outgoingRequests}
        incomingCount={incomingCount}
        onAccept={handleAcceptRequest}
        onReject={handleRejectRequest}
        onCancelRequest={async (request) => {
          try {
            setOutgoingRequests(prev => prev.filter(req => req.to_user_connect_id !== request.to_user_connect_id));

            const response = await chatAPI.cancelChatRequest(request.to_user_connect_id);
            if (response.data.success) {
              await loadOutgoingRequests();
            } else {
              await loadOutgoingRequests();
              console.error('Cancel request failed:', response.data.msg);
              showToastNotification(response.data.msg || 'failed to cancel request', 'error');
            }
          } catch (error) {
            await loadOutgoingRequests();
            console.error('Failed to cancel request:', error);
            const errorMessage = getErrorMessage(error, 'failed to cancel request');
            showToastNotification(errorMessage, 'error');
          }
        }}
      />
      
      <CreateGroupModal
        isOpen={isCreateGroupModalOpen}
        onClose={handleCloseCreateGroupModal}
        groupName={groupName}
        contacts={creatableGroupContacts}
        selectedConnectIds={selectedCreateGroupContactIds}
        error={groupCreateError}
        onGroupNameChange={(e) => setGroupName(e.target.value)}
        onToggleContact={handleToggleCreateGroupContact}
        onCreateGroup={handleCreateGroup}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            handleCloseCreateGroupModal();
          }
        }}
      />
      <GroupInvitesModal
        isOpen={isGroupInvitesModalOpen}
        onClose={() => setIsGroupInvitesModalOpen(false)}
        invites={groupInvites}
        onAccept={handleAcceptGroupInvite}
        onReject={handleRejectGroupInvite}
      />
      <GroupInfoModal
        isOpen={isGroupInfoModalOpen}
        onClose={() => setIsGroupInfoModalOpen(false)}
        groupInfo={groupInfo}
        members={groupMembers}
        pendingInvites={groupPendingInvites}
        isCreator={isGroupCreator}
        currentUserConnectId={user?.connect_id}
        onAddMembers={handleAddGroupMembers}
        onRemoveMember={handleRemoveGroupMember}
        onLeaveGroup={handleLeaveGroup}
        onRenameGroup={isGroupCreator ? handleRenameGroup : undefined}
        onCancelInvite={isGroupCreator ? handleCancelGroupInvite : undefined}
        onDeleteGroup={isGroupCreator ? handleDeleteGroupFromInfo : undefined}
        inviteContacts={inviteableGroupContacts}
      />
      
      <TrustPeerModal
        isOpen={isTrustPeerModalOpen}
        onClose={handleCloseTrustPeerModal}
        chatToTrust={chatToTrust}
        trustSettings={trustSettings}
        onTrustSettingChange={handleTrustSettingChange}
        onSelectAllTrust={handleSelectAllTrust}
        onSaveTrustSettings={handleSaveTrustSettings}
      />
      <DeleteChatModal
        isOpen={isDeleteChatModalOpen}
        onClose={handleCloseDeleteChatModal}
        chatToDelete={chatToDelete}
        onDeleteForMe={() => handleDeleteChat(false)}
        onDeleteForEveryone={() => handleDeleteChat(true)}
      />
      <DeleteMessageModal
        isOpen={isDeleteMessageModalOpen}
        onClose={handleCloseDeleteMessageModal}
        onConfirm={handleConfirmDeleteMessage}
      />
    </WorkspaceContainer>
  );
};

export default UserWorkspacePage;